import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Lock, MapPin, Trash2, FolderOpen, Target, Users,
  CheckCircle2, AlertTriangle, XCircle, Star, Sparkles, Building2,
  ListChecks, MinusCircle, Layers, Compass, Download, RefreshCw, Briefcase, Info, Share2,
} from "lucide-react";
import ShareModal from "../components/ShareModal";
import Map, { Marker } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { ECO_COLORS as ECO_MARKER_COLORS } from "../lib/ecosystemColors";
import { MAPBOX_TOKEN } from "@landhep/map-engine";
import { postDeepAnalysisV2 } from "@landhep/api-client";
import { getAICache, saveAICache, getBillingStatus, getDeepAnalysisQuote, getOwnedPotensiGrids } from "../lib/api";
import { PotensiTypeView } from "../components/potensiReports";
import CompetitorMap from "../components/CompetitorMap";
import { MapLink } from "../components/MapLink";
import { mapsUrl } from "../lib/mapsLink";
import { withinRadius } from "../lib/geo";
import { Store, Megaphone } from "lucide-react";
import { useOwnedGrids } from "../context/OwnedGridsContext";
import { idLabel } from "../lib/idLabels";
import { usePrintHold } from "../lib/printReady";
import { openDeepAnalysisPrint } from "../lib/printTicket";

// ─── Constants ────────────────────────────────────────────────────────────────

// Same 6-tier palette + labels as the Analysis map (Low…Best) so the score colour
// is consistent across map, compare cards and deep analysis.
const GRID_COLORS  = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#4ade80", "#15803d"];
const SUITABILITY  = ["Low", "Fair", "Moderate", "Good", "High", "Best"];
const SCORE_CUTS   = [20, 40, 60, 80, 90];   // absolute fallback when no per-city tier is carried
const scoreBucket  = (s) => { const v = s ?? 0; let i = 0; while (i < SCORE_CUTS.length && v >= SCORE_CUTS[i]) i++; return i; };
// Prefer the per-city tier the map assigned (carried on the zone) — falls back to
// the absolute bucket for standalone views that have no city context.
const tierIdxOf = (zone, score) => {
  if (zone && Number.isInteger(zone.suitabilityIdx)) return zone.suitabilityIdx;
  if (zone && zone.suitability) { const i = SUITABILITY.indexOf(zone.suitability); if (i >= 0) return i; }
  return scoreBucket(score);
};

const RADIUS_OPTIONS = ["1km", "3km", "5km", "7km", "10km"];

// TEMPORARY — "Daya Beli" (section 08) is hidden from the report and from the PDF at
// the CEO's request, 2026-09. Nothing is removed: the backend still computes and stores
// purchasing_power, buying_power and premium_brands, and every older report keeps the
// data it was generated with. Flip this back to `true` and both views return, PDF
// section numbering included — the numbers come from ++secNo, so hiding it renumbers
// what follows rather than leaving a gap.
const SHOW_DAYA_BELI = false;

// HIDDEN — "Kepadatan POI di Sekitar Titik" counted Google Places results, and Google
// returns at most 20 per search, so every category in a city read "20 · 11%": a plafond
// shown as a measurement (CEO, 2026-10). The backend still computes
// poi_density_google; Profil Kelurahan carries the per-category density from our own
// dataset. Flip back only once the count comes from an uncapped source (OSM).
const SHOW_POI_DENSITY_GOOGLE = false;

// Google Places returns at most 20 results per page. Ecosystem categories read one
// page, so 20 there is a floor; the competitor search reads three pages and the
// backend says when it hit its cap (`competitors_capped` / `saturation.capped`) —
// reports older than that flag were single-page, so 20 is their floor.
const GOOGLE_CAP = 20;
const capCount = (n) => (n >= GOOGLE_CAP ? `${GOOGLE_CAP}+` : String(n));
const rivalCount = (n, capped) => ((capped ?? n >= GOOGLE_CAP) ? `${n}+` : String(n));

// The printed report lists every ecosystem category, so each one is cut to its
// nearest few — the full list stays on the web tabs.
const PRINT_PLACES_PER_CATEGORY = 3;

// v2 results are persisted in deep_analysis_results.result_json (the same DB
// "ai-cache" v1 uses). An owned grid keeps a FIXED clicked coordinate, so the
// same grid+radius+context yields the same answer → call Gemini once, reuse.
const V2_CACHE_VERSION = "v3"; // v3 adds Profile Fit (Reality Lens)
const V2_CACHE_TTL     = 30 * 24 * 60 * 60 * 1000; // 30 days — Maps data drifts

// Profile fields that change the Profile-Fit result → part of the cache key.
function profileSig(p) {
  const j = (a) => (a || []).join(",");
  return [
    p?.rentalBudget, j(p?.environments), j(p?.avoidConditions),
    j(p?.primaryCustomers), j(p?.busyTimes), j(p?.priorities), j(p?.poiPriorities),
  ].join("|");
}

// The remembered "last open zone" is per profile: a shared key leaked the
// previous account's zone to whoever logged in next on the same browser.
function DEEP_ZONE_KEY() {
  try { return `landhep_pro_deep_zone::${JSON.parse(localStorage.getItem("landhep_profile") || "{}").id || "guest"}`; }
  catch { return "landhep_pro_deep_zone::guest"; }
}

function v2CacheKey(gridId, radius, subtype, targetSes, benchmarks, profile) {
  const bm = (benchmarks || []).map((b) => `${b.name}:${b.type}`).join("|");
  return `${V2_CACHE_VERSION}__${gridId}__${radius}__${subtype || "general"}__${targetSes || "all"}__${bm}__${profileSig(profile)}`;
}

function computeVerdict(score) {
  if (score >= 60) return "RECOMMENDED";
  if (score >= 40) return "PROCEED WITH CAUTION";
  return "NOT RECOMMENDED";
}

// "Your Fit" — how well this exact spot meets the operator's onboarding
// requirements, distilled from the Profile-Fit checklist (match / partial /
// mismatch). 0–10. Returns null when there's no personal profile (e.g. Lite).
function computeYourFit(summary) {
  if (!summary) return null;
  const m = summary.match || 0, p = summary.partial || 0, mm = summary.mismatch || 0;
  const denom = m + p + mm;                 // 'unknown' items are not assessable → excluded
  if (!denom) return null;
  return Math.round(((m + 0.5 * p) / denom) * 10);
}
function fitMeta(v) {
  if (v == null) return null;
  if (v >= 8) return { label: "Sangat cocok",   color: "#16a34a" };
  if (v >= 5) return { label: "Cukup cocok", color: "#d97706" };
  return        { label: "Kurang cocok",     color: "#dc2626" };
}
// On-ground verdict label → an accent colour for the scorecard chip.
function verdictTint(label) {
  return label === "Confirms" ? "#16a34a"
    : label === "Contradicts" ? "#dc2626"
    : label === "Adds Caution" ? "#d97706" : "#0f172a";
}

// Base "Business Fit" checks — always available (need only the live-map signals
// + the business category/SES), so Your Fit discriminates by location even when
// the operator's personal profile is thin. Scores the Ground Scorecard signals
// into match / partial / mismatch. Applies to both Lite and Pro.
function businessFitGroup(analysis, targetSes) {
  if (!analysis) return null;
  const items = [];

  const ogv = (analysis.on_ground_verdict?.label || "").trim();
  if (ogv) {
    const v = ogv === "Confirms" ? "match" : ogv === "Contradicts" ? "mismatch" : "partial";
    items.push({ label: "Pembacaan lapangan", verdict: v, source: "ground",
      reason: v === "match" ? "Peta langsung memastikan ini lokasi yang layak untuk dibuka."
        : v === "mismatch" ? "Peta langsung tidak mendukung untuk membuka usaha di sini."
        : "Peta langsung memberi hasil beragam — lanjutkan dengan hati-hati." });
  }

  const ci = (analysis.competition_exec_summary?.competitive_intensity || "").trim().toLowerCase();
  if (ci === "medium") items.push({ label: "Tingkat kompetisi", verdict: "match", source: "ground", reason: "Kompetisi sehat — permintaan sudah terbukti dan masih ada ruang untuk masuk." });
  else if (ci === "low") items.push({ label: "Tingkat kompetisi", verdict: "partial", source: "ground", reason: "Kompetisi rendah — peluang terbuka, tapi permintaan di sini belum banyak terbukti." });
  else if (ci === "high") items.push({ label: "Tingkat kompetisi", verdict: "mismatch", source: "ground", reason: "Kompetisi tinggi — area ini tampak jenuh untuk pemain baru." });

  const at = (analysis.area_classification?.primary_type || "").trim();
  if (at) {
    const a = at.toLowerCase();
    // Gemini writes primary_type as an Indonesian phrase; older reports carry English.
    if (/(commercial|retail|office|mixed|campus|market|tourism|downtown|business|komersial|ritel|niaga|perkantoran|kantor|campuran|kampus|pasar|wisata|pusat kota|bisnis|perbelanjaan|perdagangan)/.test(a))
      items.push({ label: "Karakter area", verdict: "match", source: "ground", reason: `${at} — lingkungan komersial yang alami.` });
    else if (/(industrial|warehouse|rural|agricult|industri|gudang|perdesaan|pedesaan|pertanian|agraris)/.test(a))
      items.push({ label: "Karakter area", verdict: "mismatch", source: "ground", reason: `${at} — lingkungan yang kurang cocok untuk usaha yang melayani pelanggan langsung.` });
    else
      items.push({ label: "Karakter area", verdict: "partial", source: "ground", reason: `${at} — masih memungkinkan, tapi bukan lingkungan komersial utama.` });
  }

  const mp = (analysis.rent_estimate?.market_position || "").trim().toLowerCase();
  if (mp) {
    const ses = (targetSes || "").toLowerCase();
    const lowOnly = ses === "low_income" || ses === "low";
    const affluent = /high|upper/.test(ses);
    const tier = mp.includes("premium") ? "premium" : mp.includes("budget") ? "budget" : "mid";
    let v, why;
    if (tier === "budget") { v = "match"; why = "Sewa kelas hemat — cukup terjangkau untuk segmen ini."; }
    else if (tier === "mid") { v = lowOnly ? "partial" : "match"; why = lowOnly ? "Sewa kelas menengah — cukup berat untuk target berpenghasilan rendah." : "Sewa kelas menengah — sesuai dengan segmen target."; }
    else { v = affluent ? "match" : lowOnly ? "mismatch" : "partial"; why = affluent ? "Sewa kelas premium — wajar untuk target kalangan atas." : lowOnly ? "Sewa kelas premium — terlalu berat untuk target berpenghasilan rendah." : "Sewa kelas premium — hanya layak dengan margin yang kuat."; }
    items.push({ label: "Keterjangkauan sewa", verdict: v, source: "ground", reason: why });
  }

  return items.length ? { group: "Kecocokan Bisnis", scope: "ground", items } : null;
}

// Merge base business-fit (always) with the personal profile-fit groups (Pro),
// and recompute one combined summary. This is what drives Your Fit + the
// Profile-Fit checklist + the PDF, so all three stay consistent.
function buildFitView(result, analysis, targetSes) {
  const bf = businessFitGroup(analysis, targetSes);
  const personal = result?.profile_fit?.groups || [];
  const groups = [...(bf ? [bf] : []), ...personal];
  if (!groups.length) return null;
  const tally = { match: 0, partial: 0, mismatch: 0, unknown: 0 };
  groups.forEach((g) => (g.items || []).forEach((it) => { tally[it.verdict] = (tally[it.verdict] || 0) + 1; }));
  const total = tally.match + tally.partial + tally.mismatch + tally.unknown;
  return { groups, summary: { ...tally, total } };
}

// On-ground verdict label (from Gemini) → palette
const VERDICT_LABEL_CFG = {
  "Confirms":     { rec: "LANJUTKAN",                        bg: "#f0fdf4", border: "#86efac", text: "#16a34a", Icon: CheckCircle2 },
  "Adds Caution": { rec: "LANJUTKAN DENGAN HATI-HATI",        bg: "#fffbeb", border: "#fde047", text: "#d97706", Icon: AlertTriangle },
  "Contradicts":  { rec: "PERTIMBANGKAN ULANG",               bg: "#fef2f2", border: "#fca5a5", text: "#dc2626", Icon: XCircle },
};

// Benchmark signal → palette
const SIGNAL_CFG = {
  GO:         { label: "LANJUT",     dot: "#16a34a", chip: "bg-green-50 text-green-700 border-green-200" },
  VALIDATION: { label: "VALIDASI",   dot: "#0d9488", chip: "bg-teal-50 text-teal-700 border-teal-200" },
  SYMBIOSIS:  { label: "SIMBIOSIS",  dot: "#2563eb", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  MODERATE:   { label: "SEDANG",     dot: "#d97706", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  NEUTRAL:    { label: "NETRAL",     dot: "#64748b", chip: "bg-slate-50 text-slate-600 border-slate-200" },
};

function loadProfile() {
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}"); }
  catch { return {}; }
}

// Compact one-line read of the onboarding age/gender matrix ({"Male|25–34": 12, …}).
// Returns e.g. "52% male · 48% female · focus ages 25–34, 18–24", or null when unset.
function summarizeAgeGroups(ag) {
  if (!ag || typeof ag !== "object") return null;
  let male = 0, female = 0;
  const byAge = {};
  for (const [k, v] of Object.entries(ag)) {
    const [g, a] = k.split("|");
    const n = Number(v) || 0;
    if (g === "Male") male += n; else if (g === "Female") female += n;
    if (a) byAge[a] = (byAge[a] || 0) + n;
  }
  const total = male + female;
  if (total <= 0) return null;
  const topAges = Object.entries(byAge).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([a]) => a);
  return `${Math.round(male / total * 100)}% laki-laki · ${Math.round(female / total * 100)}% perempuan · fokus usia ${topAges.join(", ")}`;
}

// Distil the onboarding profile into display blocks reused by both the on-page
// "Business Profile" section and the PDF, so the two never drift apart.
function buildProfileView(profile, ctx = {}) {
  const p = profile || {};
  const city = p.city && typeof p.city === "object" ? p.city : {};
  const cityStr = [city.nama_kabkot, city.nama_provinsi].filter(Boolean).join(", ");
  const ageSummary = summarizeAgeGroups(p.ageGroups);

  const scalars = [
    ["Nama Bisnis", p.businessName],
    ["Jenis Bisnis", ctx.subcategory || p.bizSubtype],
    ["Kota Target", cityStr],
    ["Anggaran Sewa Bulanan", p.rentalBudget],
    ["Kelas Harga / Pendapatan", p.marketTier],
    ["Fokus Usia & Gender", ageSummary],
  ].filter(([, v]) => v != null && String(v).trim() !== "");

  const chipGroups = [
    ["Hal yang paling penting", p.priorities],
    ["Lingkungan yang disukai", p.environments],
    ["Kondisi yang dihindari", p.avoidConditions],
    ["Prioritas POI", p.poiPriorities],
    ["Pelanggan utama", p.primaryCustomers],
    ["Waktu tersibuk", p.busyTimes],
  ].filter(([, v]) => Array.isArray(v) && v.length > 0);

  const benchmarks = (p.benchmarks || []).filter((b) => (b?.name || "").trim());

  return {
    scalars, chipGroups, benchmarks,
    hasAnything: scalars.length > 0 || chipGroups.length > 0 || benchmarks.length > 0,
  };
}

// "Sunday, 21 June 2026" from a ms-epoch / ISO date.
function fmtGenerated(v) {
  if (!v) return null;
  const d = new Date(typeof v === "number" ? v : Date.parse(v));
  if (isNaN(d)) return null;
  const wd = d.toLocaleDateString("id-ID", { weekday: "long" });
  const rest = d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  return `${wd}, ${rest}`;
}
const co = (n) => (n == null ? "—" : Number(n).toFixed(5));

// ─── UI primitives ──────────────────────────────────────────────────────────────

function Spinner() {
  return <div className="w-5 h-5 border-2 border-[#0d9488] border-t-transparent rounded-full animate-spin" />;
}

// Full-screen blocking overlay shown while the (billed) Gemini Maps call runs.
// Covers everything — including the top nav — so the user can't navigate away
// and waste the paid analysis. Pairs with a beforeunload guard.
function ScanningOverlay({ refreshing, radius }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
      style={{ background: "radial-gradient(circle at 50% 40%, rgba(6,46,43,0.92), rgba(2,12,18,0.97))", backdropFilter: "blur(6px)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative flex flex-col items-center text-center max-w-md">
        {/* Radar pulse */}
        <div className="relative w-28 h-28 mb-8">
          <span className="absolute inset-0 rounded-full bg-teal-400/20 animate-ping" style={{ animationDuration: "1.8s" }} />
          <span className="absolute inset-3 rounded-full bg-teal-400/20 animate-ping" style={{ animationDuration: "1.8s", animationDelay: "0.4s" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-2xl shadow-teal-500/40">
              <Compass className="w-8 h-8 text-white animate-spin" style={{ animationDuration: "3s" }} />
            </div>
          </div>
        </div>

        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-teal-300/80 mb-2">
          {refreshing ? "Memperbarui data intelijen" : "Pemindaian lapangan langsung"}
        </p>
        <h2 className="text-2xl font-extrabold text-white mb-3 leading-snug">
          Memindai wilayah<br />di sekitar titik Anda…
        </h2>
        <p className="text-sm text-slate-300/90 leading-relaxed mb-6">
          Kami sedang membaca data Google Maps secara langsung dalam radius {radius || "yang dipilih"} dari titik yang Anda klik —
          kompetitor, ekosistem, karakter area, dan lainnya. Proses ini biasanya memakan waktu <span className="font-semibold text-white">20–40 detik</span>.
        </p>

        <div className="flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-4 py-2">
          <Lock className="w-3.5 h-3.5 text-amber-300" />
          <span className="text-xs font-medium text-amber-200">Jangan tutup halaman ini — keluar sekarang akan membatalkan analisis.</span>
        </div>

        {/* Indeterminate progress shimmer */}
        <div className="mt-8 h-1 w-56 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-teal-300 to-transparent animate-[scanbar_1.4s_ease-in-out_infinite]" />
        </div>
      </div>

      <style>{`@keyframes scanbar { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }`}</style>
    </div>
  );
}

function ScoreGauge({ score, idx }) {
  const color = GRID_COLORS[idx];
  const r = 52, circ = 2 * Math.PI * r;
  const filled = circ * (score / 100);
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="10" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-extrabold text-white leading-none">{score}</span>
          <span className="text-[11px] text-white/60 mt-0.5">/100</span>
        </div>
      </div>
      <span className="text-xs font-bold px-3 py-1 rounded-full text-white/70 bg-white/10 tracking-wide">
        Kesesuaian: {idLabel(SUITABILITY[idx])}
      </span>
    </div>
  );
}

// Mirrors ScoreGauge, but for the personal "Your Fit" score (0–10).
function FitGauge({ value }) {
  const meta = fitMeta(value) || { label: "—", color: "#94a3b8" };
  const r = 52, circ = 2 * Math.PI * r;
  const filled = circ * (Math.max(0, Math.min(10, value)) / 10);
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="10" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={meta.color} strokeWidth="10"
            strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-extrabold text-white leading-none">{value}</span>
          <span className="text-[11px] text-white/60 mt-0.5">/10</span>
        </div>
      </div>
      <span className="text-xs font-bold px-3 py-1 rounded-full text-white/70 bg-white/10 tracking-wide">
        {meta.label}
      </span>
    </div>
  );
}

// Compact "quick read" of the Gemini deep analysis — 4 categorical signals that
// break ties between two grids that share the same numeric Site Score.
function GroundScorecard({ analysis }) {
  const items = [
    { label: "Vonis Lapangan", value: idLabel(analysis?.on_ground_verdict?.label), tint: verdictTint(analysis?.on_ground_verdict?.label) },
    { label: "Kompetisi",       value: idLabel(analysis?.competition_exec_summary?.competitive_intensity) },
    { label: "Kelas Sewa",         value: idLabel(analysis?.rent_estimate?.market_position) },
    { label: "Jenis Area",         value: analysis?.area_classification?.primary_type },
  ];
  return (
    <div className="da-card rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks className="w-4 h-4 text-teal-600" />
        <h3 className="text-sm font-bold text-slate-800">Kartu Skor Lapangan</h3>
        <span className="text-[11px] text-slate-400">— rangkuman cepat dari peta langsung</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{it.label}</p>
            <p className="text-sm font-extrabold" style={{ color: it.value ? (it.tint || "#0f172a") : "#cbd5e1" }}>{it.value || "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function VerdictBadge({ verdict, ground }) {
  const cv = combinedVerdict(verdict, ground);
  const cfg = cv && {
    good: { Icon: CheckCircle2,  bg: "#dcfce7", color: "#16a34a", border: "#86efac" },
    warn: { Icon: AlertTriangle, bg: "#fefce8", color: "#ca8a04", border: "#fde047" },
    bad:  { Icon: XCircle,       bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" },
  }[cv.tone];
  if (!cfg) return null;
  return (
    <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl border-2"
      style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}>
      <cfg.Icon className="w-5 h-5 shrink-0" style={{ color: cfg.color }} />
      <span className="text-sm font-extrabold tracking-widest uppercase" style={{ color: cfg.color }}>{verdict}</span>
      {cv.suffix && <span className="text-[11px] font-semibold" style={{ color: cfg.color }}>· {cv.suffix}</span>}
    </div>
  );
}

// `long`: in print, a section that may span pages breaks between its inner cards
// and rows instead of being pushed whole onto the next page (public/print.css).
export function Section({ title, subtitle, icon: Icon, children, long = false }) {
  return (
    <div className={`da-card ${long ? "da-long" : ""} bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden`}>
      <div className="da-card-head px-6 py-4 border-b border-gray-50 flex items-center gap-3">
        {Icon && (
          <div className="w-7 h-7 rounded-lg bg-[#ccfbf1] flex items-center justify-center shrink-0">
            <Icon className="w-3.5 h-3.5 text-[#0d9488]" />
          </div>
        )}
        <div>
          <p className="text-xs font-bold text-gray-800">{title}</p>
          {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Business profile recap (from onboarding) ──────────────────────────────────

function BusinessProfile({ view }) {
  const { scalars, chipGroups, benchmarks, hasAnything } = view;
  if (!hasAnything) {
    return (
      <p className="text-sm text-slate-400">
        Belum ada profil bisnis tersimpan. Lengkapi onboarding untuk mempersonalisasi laporan ini.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      {scalars.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          {scalars.map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-4 py-2 border-b border-slate-100">
              <span className="text-[11px] font-medium text-slate-400 shrink-0">{k}</span>
              <span className="text-[12px] font-semibold text-slate-700 text-right">{v}</span>
            </div>
          ))}
        </div>
      )}

      {chipGroups.map(([label, values]) => (
        <div key={label}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{label}</p>
          <div className="flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span key={v} className="px-2.5 py-1 rounded-full bg-slate-100 text-[11px] font-medium text-slate-600">{idLabel(v)}</span>
            ))}
          </div>
        </div>
      ))}

      {benchmarks.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Benchmark / Acuan</p>
          <div className="flex flex-wrap gap-1.5">
            {benchmarks.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 text-[11px] font-medium text-teal-700">
                {b.name}{b.type && <span className="text-teal-400 font-normal">· {idLabel(b.type)}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stars({ rating, reviews }) {
  if (rating == null) return <span className="text-[11px] text-slate-300">Belum ada rating</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600">
      <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {rating}
      {reviews != null && <span className="text-slate-400 font-normal">({reviews.toLocaleString?.() ?? reviews})</span>}
    </span>
  );
}

// ─── On-ground verdict (Gemini label) ──────────────────────────────────────────

function OnGroundVerdict({ verdict }) {
  if (!verdict?.label) return null;
  const cfg = VERDICT_LABEL_CFG[verdict.label] || VERDICT_LABEL_CFG["Adds Caution"];
  return (
    <div className="da-card rounded-2xl border-2 p-5" style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}>
      <div className="flex items-center gap-3 mb-2.5">
        <cfg.Icon className="w-7 h-7 shrink-0" style={{ color: cfg.text }} />
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Rekomendasi</p>
          <p className="text-xl font-extrabold leading-none" style={{ color: cfg.text }}>{cfg.rec}</p>
        </div>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full"
          style={{ color: cfg.text, backgroundColor: "#ffffff99", border: `1px solid ${cfg.border}` }}>
          {idLabel(verdict.label)}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-slate-700">{verdict.summary}</p>
    </div>
  );
}

// ─── Benchmark match cards ──────────────────────────────────────────────────────

function BenchmarkMatch({ matches }) {
  if (!matches?.length) return <p className="text-sm text-slate-400">Belum ada benchmark yang ditambahkan di profil Anda.</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {matches.map((m, i) => {
        const sig = SIGNAL_CFG[m.signal] || SIGNAL_CFG.NEUTRAL;
        return (
          <div key={i} className="rounded-xl border border-slate-150 bg-slate-50/60 p-4">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div>
                <p className="text-sm font-bold text-slate-800">{m.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">{idLabel(m.type)}</p>
              </div>
              <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${sig.chip}`}>
                {sig.label}
              </span>
            </div>
            <p className="text-[12px] leading-relaxed text-slate-600 mb-2">{m.interpretation}</p>
            {m.places?.length > 0 ? (
              <div className="space-y-1.5 border-t border-slate-100 pt-2">
                {m.places.map((p, j) => (
                  <div key={j} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-slate-700 truncate">{p.name}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-slate-400">{p.distance_label}</span>
                      <Stars rating={p.rating} reviews={p.reviews} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-2">
                Tidak ditemukan dalam radius ini.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Nearby Competitor Analysis ─────────────────────────────────────────────────

const THREAT_CFG = {
  High:   { text: "#dc2626" },
  Medium: { text: "#d97706" },
  Low:    { text: "#16a34a" },
};
const INTENSITY_CFG = {
  High:   { text: "#dc2626", bar: "100%" },
  Medium: { text: "#d97706", bar: "60%" },
  Low:    { text: "#16a34a", bar: "30%" },
};

function ExecCard({ label, children }) {
  return (
    <div className="rounded-xl bg-white border border-slate-100 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function CompetitorAnalysis({ execSummary, competitors, actionPlan, center, printMode = false }) {
  const es = execSummary || {};
  const intensity = es.competitive_intensity || "—";
  const ic = INTENSITY_CFG[intensity] || { text: "#64748b", bar: "50%" };
  return (
    <div className="space-y-5">
      {/* Executive Summary */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <ExecCard label="Intensitas Kompetisi">
          <p className="text-xl font-extrabold leading-none" style={{ color: ic.text }}>{idLabel(intensity)}</p>
          <div className="mt-2 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: ic.bar, backgroundColor: ic.text }} />
          </div>
        </ExecCard>
        <ExecCard label="Kompetitor Signifikan">
          <p className="text-xl font-extrabold text-slate-800 leading-none">{es.significant_competitors ?? "—"}</p>
        </ExecCard>
        <ExecCard label="Risiko Utama">
          <p className="text-xs text-slate-600 leading-snug">{es.key_risks || "—"}</p>
        </ExecCard>
        <ExecCard label="Peluang Diferensiasi">
          <p className="text-xs text-slate-600 leading-snug">{es.differentiation_opportunities || "—"}</p>
        </ExecCard>
      </div>

      {/* Competitors on their own map. The overview map has to carry every ecosystem
          category at the full radius, which shrinks the rivals to a dot in the middle;
          here they get the whole frame, numbered to match the table row for row. */}
      <CompetitorMap center={center} competitors={competitors || []}
        numbered={(competitors || []).length} staticImage={printMode} />

      {/* Table */}
      {competitors?.length ? (
        <div className="overflow-x-auto rounded-xl border border-slate-150">
          <table className="w-full text-left min-w-[720px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-3 py-2.5 font-bold">Kompetitor</th>
                <th className="px-3 py-2.5 font-bold">Tipe</th>
                <th className="px-3 py-2.5 font-bold">Jarak</th>
                <th className="px-3 py-2.5 font-bold">Ancaman</th>
                <th className="px-3 py-2.5 font-bold">Keunggulan Utama</th>
                <th className="px-3 py-2.5 font-bold">Potensi Dampak</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {competitors.map((c, i) => {
                const tc = THREAT_CFG[c.threat_level] || { text: "#64748b" };
                return (
                  <tr key={i} className="align-top">
                    <td className="px-3 py-3">
                      <div className="border-l-2 pl-2.5" style={{ borderColor: tc.text }}>
                        <p className="text-[13px] font-bold text-slate-800">
                          <MapLink place={c}>{c.name}</MapLink>
                        </p>
                        {c.subtitle && <p className="text-[9px] uppercase tracking-wider text-slate-400 mb-0.5">{c.subtitle}</p>}
                        {c.rating != null && <Stars rating={c.rating} reviews={c.reviews} />}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{idLabel(c.type)}</span>
                    </td>
                    <td className="px-3 py-3 text-[12px] text-slate-500 whitespace-nowrap">{c.distance_label}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-[12px] font-bold" style={{ color: tc.text }}>
                        {idLabel(c.threat_level)}{c.threat_score != null ? ` (${c.threat_score})` : ""}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(c.strengths || []).map((s, j) => (
                          <span key={j} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[12px] text-slate-500 max-w-[220px]">{c.potential_impact}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400">Tidak ada kompetitor ditemukan dalam radius ini.</p>
      )}

      {/* Action plan */}
      {actionPlan?.length > 0 && (
        <div className="rounded-xl border border-slate-150 p-5">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-teal-700 mb-4">Rencana Aksi yang Disarankan</p>
          <div className="space-y-3">
            {actionPlan.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-extrabold flex items-center justify-center mt-0.5">{i + 1}</span>
                <p className="text-[13px] text-slate-700 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Area & Property + Ecosystem ───────────────────────────────────────────────

// The ticket behind the Throughput table: the operator's own basket when the
// backend had one (rivals sell the same thing), else the area's price band.
function ticketLabel(a) {
  const [lo, hi] = a?.avg_ticket_idr || [];
  if (lo == null) return "tiket tidak diketahui";
  const rp = (n) => `Rp ${Number(n).toLocaleString("id-ID")}`;
  if (a.ticket_source === "basket" || lo === hi) return `tiket ${rp(lo)} per kunjungan (belanja dari profil Anda)`;
  return `tiket ${rp(lo)}–${rp(hi)} (kelas harga area)`;
}

function fmtIDR(n) {
  if (n == null) return "—";
  return "Rp " + Number(n).toLocaleString("id-ID");
}

function AreaProperty({ classification, rent }) {
  return (
    <div className="space-y-4">
      {rent && (rent.avg_per_sqm_month_idr != null || rent.range_low_idr != null) && (
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rata-rata Sewa</p>
              <p className="text-2xl font-extrabold text-slate-800">
                {fmtIDR(rent.avg_per_sqm_month_idr)}<span className="text-xs font-medium text-slate-400">/m²/bln</span>
              </p>
            </div>
            {rent.is_estimate && (
              <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">ESTIMASI AI</span>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] text-slate-400">Rentang</p>
              <p className="text-xs font-mono text-slate-600">{fmtIDR(rent.range_low_idr)} – {fmtIDR(rent.range_high_idr)}/m²</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400">Posisi Pasar</p>
              <p className="text-xs font-bold text-teal-700">{idLabel(rent.market_position) || "—"}</p>
            </div>
          </div>
          {rent.basis && <p className="mt-2 text-[10px] text-slate-400 italic">Dasar: {rent.basis}</p>}
        </div>
      )}
      {classification && (
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-700">Klasifikasi Area</p>
            {classification.confidence != null && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Keyakinan {classification.confidence}%</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Jenis Utama</p>
              <p className="text-sm font-bold text-slate-800">{classification.primary_type || "—"}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Jenis Sekunder</p>
              <p className="text-sm font-bold text-slate-800">{classification.secondary_type || "—"}</p>
            </div>
          </div>
          {classification.narrative && (
            <p className="mt-3 text-xs leading-relaxed text-slate-500 italic">"{classification.narrative}"</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Report map: the real places from Places API plotted around the clicked point ──
// Single source of truth for the profile whitelist sent to /deep-analysis-v2.
// EVERY deep-analysis entry point (this page AND Branch Radar) must use this, or a
// field added here silently fails to reach the report from one of them — which is
// exactly how Trade Areas went missing on Radar. Add new profile fields ONCE, here.
export function buildProfileForFit(profile, targetSes) {
  return {
    rentalBudget:        profile?.rentalBudget,
    target_ses:          targetSes ?? profile?.target_ses,
    environments:        profile?.environments,
    avoidConditions:     profile?.avoidConditions,
    primaryCustomers:    profile?.primaryCustomers,
    ageGroups:           profile?.ageGroups,
    busyTimes:           profile?.busyTimes,
    priorities:          profile?.priorities,
    poiPriorities:       profile?.poiPriorities,
    // Trade Areas
    basketSize:          profile?.basketSize,
    buyingFrequency:     profile?.buyingFrequency,
    buyingFrequencyUnit: profile?.buyingFrequencyUnit,
    marketExpectation:   profile?.marketExpectation,
    productPriceMin:     profile?.productPriceMin,
    productPriceMax:     profile?.productPriceMax,
  };
}


function MapLegend({ color, label, off = false, onClick }) {
  const cls = `inline-flex items-center gap-1.5 transition-opacity ${off ? "opacity-35 line-through" : "text-slate-500"}`;
  const dot = <span className="w-2.5 h-2.5 rounded-full border border-white shadow-sm" style={{ background: color }} />;
  if (!onClick) return <span className={cls}>{dot}{label}</span>;
  return (
    <button type="button" onClick={onClick} className={`${cls} cursor-pointer hover:opacity-100`} title={off ? "Tampilkan" : "Sembunyikan"}>
      {dot}{label}
    </button>
  );
}

const COMP_KEY = "__competitors__";

// Mapbox paints asynchronously, so a capture taken right after fitBounds would
// still hold the previous frame. Wait for one idle event, but never let a tile
// that refuses to load block the PDF.
function mapSettled(map, ms = 1500) {
  return new Promise((resolve) => {
    const done = () => { clearTimeout(t); map.off("idle", done); resolve(); };
    const t = setTimeout(done, ms);
    map.once("idle", done);
  });
}

// `staticImage` (print): once the tiles have painted, the live map is replaced by
// its own snapshot — the print dialog gets a plain <img>, not a WebGL canvas —
// and the page is held until that snapshot exists (lib/printReady.jsx).
export function ReportMap({ lat, lng, competitors = [], ecosystem = [], staticImage = false }) {
  const mapRef = useRef(null);
  const [active, setActive] = useState(null);
  const [snap, setSnap] = useState(null);
  const releaseHold = usePrintHold(staticImage);
  // Legend doubles as a filter: click a category to hide/show it. All visible by
  // default; the set holds the HIDDEN keys. With ~15 ecosystem categories the map
  // would otherwise be unreadable, so this is how the density stays legible.
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (key) => setHidden((h) => {
    const next = new Set(h);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const hasPoint = lat != null && lng != null;
  const compVisible = !hidden.has(COMP_KEY);
  const compPtsAll = competitors.filter((c) => c.lat != null && c.lng != null);
  const compPts = compVisible ? compPtsAll : [];
  const ecoPts = ecosystem.flatMap((e) =>
    (e.places || []).filter((p) => p.lat != null && p.lng != null).map((p) => ({ ...p, category: e.category })))
    .filter((p) => !hidden.has(p.category));
  const all = [...(hasPoint ? [{ lat, lng }] : []), ...compPtsAll, ...ecoPts];

  const center = all.length ? (hasPoint ? { lat, lng } : {
    lat: all.reduce((s, p) => s + p.lat, 0) / all.length,
    lng: all.reduce((s, p) => s + p.lng, 0) / all.length,
  }) : { lat: 0, lng: 0 };

  function fitAll() {
    const m = mapRef.current?.getMap?.();
    if (!m || all.length < 2) return;
    const lats = all.map((p) => p.lat), lngs = all.map((p) => p.lng);
    m.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 48, maxZoom: 16, duration: 0 },
    );
  }

  // Bounds of the dense core: the nearest 90% of plotted points. One far-flung POI
  // otherwise drags the frame out until everything in the middle is unreadable.
  function coreBounds() {
    const pts = all.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (pts.length < 2) return null;
    const c = hasPoint ? { lat, lng } : center;
    const kx = Math.cos((c.lat * Math.PI) / 180);
    const keep = pts
      .map((p) => ({ p, d: Math.hypot(p.lat - c.lat, (p.lng - c.lng) * kx) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, Math.max(2, Math.ceil(pts.length * 0.9)))
      .map((x) => x.p);
    const lats = keep.map((p) => p.lat), lngs = keep.map((p) => p.lng);
    return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
  }

  // Snapshot the rendered basemap canvas + redraw markers on top (via map.project),
  // so the PDF gets the same picture WITHOUT any new Mapbox API call. The on-screen
  // view can be panned anywhere and is fitted to the farthest POI, so the capture
  // frames the core itself first, then puts the user's view back.
  async function capture() {
    const m = mapRef.current?.getMap?.();
    if (!m) return null;
    const prev = { center: m.getCenter(), zoom: m.getZoom() };
    try {
      const core = coreBounds();
      if (core) {
        m.fitBounds(core, { padding: 36, maxZoom: 17, duration: 0 });
        await mapSettled(m);
      }
      const src = m.getCanvas();
      const w = src.width, h = src.height;
      if (!w || !h) return null;
      const off = document.createElement("canvas");
      off.width = w; off.height = h;
      const ctx = off.getContext("2d");
      ctx.drawImage(src, 0, 0);
      const dpr = w / (m.getContainer().clientWidth || w);
      const dot = (plng, plat, color, r) => {
        const pt = m.project([plng, plat]);
        ctx.beginPath();
        ctx.arc(pt.x * dpr, pt.y * dpr, r * dpr, 0, 2 * Math.PI);
        ctx.fillStyle = color; ctx.fill();
        ctx.lineWidth = 1.5 * dpr; ctx.strokeStyle = "#fff"; ctx.stroke();
      };
      ecoPts.forEach((p) => dot(p.lng, p.lat, ECO_MARKER_COLORS[p.category] || "#64748b", 4));
      compPts.forEach((c) => dot(c.lng, c.lat, "#ef4444", 5));
      if (hasPoint) dot(lng, lat, "#0f766e", 7);
      return { dataUrl: off.toDataURL("image/png"), w, h };
    } catch {
      return null;
    } finally {
      m.jumpTo({ center: prev.center, zoom: prev.zoom });
    }
  }
  async function onLoad() {
    const m = mapRef.current?.getMap?.();
    // The canvas keeps the size it initialised at; if the container was resized
    // by a stylesheet that arrived later, the snapshot would be the stale size.
    if (staticImage && m) m.resize();
    fitAll();
    if (!staticImage) return;
    if (m) { await mapSettled(m, 4000); setSnap(await capture()); }
    releaseHold();   // a failed snapshot keeps the live map; never block the print
  }

  if (all.length === 0) return null;

  const ecoCatsOnMap = ecosystem.filter((e) => (e.places || []).some((p) => p.lat != null));

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200" style={{ height: 380 }}>
        {snap ? (
          <img src={snap.dataUrl} alt="Peta lokasi" className="w-full h-full object-cover" />
        ) : (
        <Map
          ref={mapRef}
          onLoad={onLoad}
          onError={() => { if (staticImage) releaseHold(); }}
          initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 14 }}
          mapStyle="mapbox://styles/mapbox/light-v11"
          mapboxAccessToken={MAPBOX_TOKEN}
          attributionControl={false}
          preserveDrawingBuffer
        >
          {hasPoint && (
            <Marker longitude={lng} latitude={lat}>
              <div className="w-5 h-5 rounded-full bg-teal-700 border-[2.5px] border-white shadow-lg" title="Titik Anda" />
            </Marker>
          )}
          {compPts.map((c, i) => (
            <Marker key={`c${i}`} longitude={c.lng} latitude={c.lat}>
              <div
                onMouseEnter={() => setActive({ ...c, kind: "Kompetitor", color: "#ef4444" })}
                onMouseLeave={() => setActive(null)}
                onClick={() => { const u = mapsUrl(c); if (u) window.open(u, "_blank", "noopener,noreferrer"); }}
                className="w-3 h-3 rounded-full border border-white shadow cursor-pointer hover:scale-125 transition-transform"
                style={{ background: "#ef4444" }}
              />
            </Marker>
          ))}
          {ecoPts.map((p, i) => (
            <Marker key={`e${i}`} longitude={p.lng} latitude={p.lat}>
              <div
                onMouseEnter={() => setActive({ ...p, kind: idLabel(p.category), color: ECO_MARKER_COLORS[p.category] || "#64748b" })}
                onMouseLeave={() => setActive(null)}
                onClick={() => { const u = mapsUrl(p); if (u) window.open(u, "_blank", "noopener,noreferrer"); }}
                className="w-2.5 h-2.5 rounded-full border border-white shadow cursor-pointer hover:scale-125 transition-transform"
                style={{ background: ECO_MARKER_COLORS[p.category] || "#64748b" }}
              />
            </Marker>
          ))}
        </Map>
        )}
        {active && (
          <div className="absolute top-3 left-3 rounded-lg bg-white/95 backdrop-blur px-3 py-2 shadow-lg border border-slate-200 max-w-[240px] pointer-events-none">
            <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: active.color }}>{active.kind}</p>
            <p className="text-xs font-semibold text-slate-800 truncate">{active.name}</p>
            {(active.rating != null || active.distance_label) && (
              <p className="text-[10px] text-slate-400">
                {active.rating != null ? `${active.rating}★` : ""}
                {active.rating != null && active.distance_label ? " · " : ""}
                {active.distance_label || ""}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px]">
        {hasPoint && <MapLegend color="#0f766e" label="Titik Anda" />}
        {compPtsAll.length > 0 && (
          <MapLegend color="#ef4444" label={`Kompetitor (${compPtsAll.length})`}
            off={hidden.has(COMP_KEY)} onClick={staticImage ? undefined : () => toggle(COMP_KEY)} />
        )}
        {ecoCatsOnMap.map((e) => (
          <MapLegend key={e.category} color={ECO_MARKER_COLORS[e.category] || "#64748b"}
            label={idLabel(e.category)} off={hidden.has(e.category)} onClick={staticImage ? undefined : () => toggle(e.category)} />
        ))}
        {hidden.size > 0 && !staticImage && (
          <button onClick={() => setHidden(new Set())}
            className="text-[10px] font-medium text-teal-600 hover:underline">Tampilkan semua</button>
        )}
      </div>
    </div>
  );
}

// The tab chip; `on` = the category whose places are shown below it.
function EcosystemTab({ cat, on, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${on ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
      {idLabel(cat.category)}
      <span className={`text-[10px] px-1.5 rounded-full ${on ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{cat.source === "osm" ? (cat.count ?? cat.places?.length ?? 0) : capCount(cat.count ?? cat.places?.length ?? 0)}</span>
    </Tag>
  );
}

// `printMode`: paper has no tabs, so every category is listed in turn, each
// under its own (inert) tab chip, so the blocks still look like the web view.
export function Ecosystem({ ecosystem, radius, printMode = false }) {
  const cats = (ecosystem || []).filter(Boolean);
  const [active, setActive] = useState(0);
  if (!cats.length) return <p className="text-sm text-slate-400">Tidak ada POI pendukung ditemukan dalam radius ini.</p>;
  if (printMode) {
    return (
      <div className="space-y-6">
        {cats.map((c, i) => (
          <div key={c.category ?? i}>
            <div className="flex flex-wrap gap-2 mb-3"><EcosystemTab cat={c} on /></div>
            <EcosystemPlaces cat={c} radius={radius} limit={PRINT_PLACES_PER_CATEGORY} />
          </div>
        ))}
      </div>
    );
  }
  const cur = cats[Math.min(active, cats.length - 1)];
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {cats.map((c, i) => <EcosystemTab key={c.category ?? i} cat={c} on={active === i} onClick={() => setActive(i)} />)}
      </div>
      <EcosystemPlaces cat={cur} radius={radius} />
    </div>
  );
}

// `limit`: print caps the cards per category so the PDF stays readable.
function EcosystemPlaces({ cat: cur, radius, limit }) {
  const places = limit ? (cur.places || []).slice(0, limit) : (cur.places || []);
  const shown = places.length;
  const total = cur.count ?? (cur.places?.length ?? 0);
  // One Google search returns at most 20 — "20" is a floor, never the real count.
  const capped = cur.source !== "osm" && total >= GOOGLE_CAP;
  return (
    <div>
      {shown > 0 && (
        <p className="text-[11px] text-slate-400 mb-3">
          Menampilkan {shown} terdekat dari {capped ? `${GOOGLE_CAP}+` : `~${total}`} dalam radius {radius || "yang dipilih"}
          {capped && " · Google hanya mengembalikan 20 hasil per pencarian, jumlah sebenarnya bisa lebih banyak"}
        </p>
      )}
      {places.length ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {places.map((p, i) => (
            <div key={i} className="rounded-xl border border-slate-150 p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-slate-800"><MapLink place={p}>{p.name}</MapLink></p>
                {p.rating != null && (
                  <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />{p.rating}
                  </span>
                )}
              </div>
              {p.address && (
                <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-300 shrink-0" />{p.address}
                </p>
              )}
              {p.distance_label && <p className="mt-1 text-[10px] text-slate-400">{p.distance_label}</p>}
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-slate-400">Tidak ada {cur.category} ditemukan dalam radius ini.</p>}
    </div>
  );
}

// ─── Profile Fit (Reality Lens) ────────────────────────────────────────────────

const VERDICT_UI = {
  match:    { Icon: CheckCircle2,  color: "#16a34a", bg: "#f0fdf4" },
  partial:  { Icon: AlertTriangle, color: "#d97706", bg: "#fffbeb" },
  mismatch: { Icon: XCircle,       color: "#dc2626", bg: "#fef2f2" },
  unknown:  { Icon: MinusCircle,   color: "#94a3b8", bg: "#f8fafc" },
};

function scopeNote(scope) {
  if (scope === "1km")        return "Cek lokasi · dalam 1 km";
  if (scope === "kelurahan")  return "Data kelurahan";
  if (scope === "estimate")   return "Estimasi AI";
  if (scope === "estimate · typical size") return "Estimasi AI · ukuran umum";
  if (scope === "mixed")      return "Kelurahan + peta langsung";
  if (scope === "ground")     return "Cek peta langsung";
  if (scope === "area POIs")  return "Dari POI terdekat · peta langsung";
  if (scope === "hourly")     return "Lalu lintas langsung · per jam";
  if (/^\d+km$/.test(scope || "")) return `Peta langsung · dalam ${scope}`;
  if (/^\d/.test(scope || "")) return `Dalam ${scope}`;
  return null;
}

// The two-lens explainer — Site Score (Data Lens) vs Profile Fit (Reality Lens).
function TwoLensNote() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 mb-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-200/70 flex items-center justify-center shrink-0">
            <Layers className="w-3.5 h-3.5 text-slate-600" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-700">Skor Lokasi — Lensa Data</p>
            <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
              Model statistik dari data nasional. Menjawab: <em>bagaimana peringkat lokasi ini
              dibanding lokasi lain</em> untuk jenis usaha Anda? Bersifat umum &amp; komparatif.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
            <Compass className="w-3.5 h-3.5 text-teal-700" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-700">Profile Fit — Lensa Kenyataan</p>
            <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
              Mencocokkan kebutuhan spesifik <em>Anda</em> dengan kondisi nyata di titik ini.
              Menjawab: <em>apakah lokasi ini cocok untuk usaha SAYA?</em> Bersifat personal &amp; kontekstual.
            </p>
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 leading-snug mt-3 pt-3 border-t border-slate-200">
        Kedua lensa ini bisa saja berbeda hasil — area yang kuat secara statistik bisa saja tidak
        memenuhi kebutuhan personal Anda, begitu pula sebaliknya. Baca keduanya bersamaan, bukan
        sebagai pengganti satu sama lain.
      </p>
    </div>
  );
}

function FitRow({ item }) {
  const ui = VERDICT_UI[item.verdict] || VERDICT_UI.unknown;
  return (
    <div className="flex items-start gap-2.5 py-2">
      <ui.Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: ui.color }} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-slate-800 leading-tight">{idLabel(item.label)}</p>
        {item.reason && <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{item.reason}</p>}
      </div>
    </div>
  );
}

function FitGroupCard({ g }) {
  const note = scopeNote(g.scope);
  const is1km = g.scope === "1km" || g.scope === "mixed";
  return (
    <div className="rounded-xl bg-slate-50/60 p-4">
      <div className="flex items-center justify-between gap-2 mb-1 pb-2 border-b border-slate-100/80">
        <p className="text-xs font-bold text-slate-700">{idLabel(g.group)}</p>
        {note && (
          <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
            is1km ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-400"
          }`}>
            {note}
          </span>
        )}
      </div>
      <div className="divide-y divide-slate-100/70">
        {g.items.map((it, j) => <FitRow key={j} item={it} />)}
      </div>
    </div>
  );
}

function ProfileFit({ fit }) {
  if (!fit?.groups?.length) return null;
  const s = fit.summary || {};
  // POI Priorities gets its own full-width row with a 2-column item list.
  const poiGroup = fit.groups.find((g) => g.group === "POI Priorities");
  const otherGroups = fit.groups.filter((g) => g.group !== "POI Priorities");

  return (
    <div>
      <TwoLensNote />

      {/* Summary tally */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          ["match", "Cocok", s.match],
          ["partial", "Sebagian", s.partial],
          ["mismatch", "Tidak cocok", s.mismatch],
          ...(s.unknown ? [["unknown", "Tanpa data", s.unknown]] : []),
        ].map(([k, label, n]) => {
          const ui = VERDICT_UI[k];
          return (
            <span key={k} className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
              style={{ backgroundColor: ui.bg, color: ui.color }}>
              <ui.Icon className="w-3 h-3" /> {n ?? 0} {label}
            </span>
          );
        })}
      </div>

      {/* Requirement groups (2-col) */}
      <div className="grid gap-3 sm:grid-cols-2">
        {otherGroups.map((g, i) => <FitGroupCard key={i} g={g} />)}
      </div>

      {/* POI Priorities — full width, items in 2 columns */}
      {poiGroup && (
        <div className="mt-3 rounded-xl bg-slate-50/60 p-4">
          <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-slate-100/80">
            <p className="text-xs font-bold text-slate-700">{idLabel(poiGroup.group)}</p>
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-50 text-teal-600">
              {scopeNote(poiGroup.scope)}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6">
            {poiGroup.items.map((it, j) => <FitRow key={j} item={it} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DB facts: Population by group + Neighbourhood profile ──────────────────────

const AGE_COLORS = ["#93c5fd", "#6ee7b7", "#fbbf24", "#a78bfa", "#fb923c", "#f87171"];

export function PopulationByGroup({ demo }) {
  if (!demo?.pop_total) return null;
  const r = (n) => Number(n || 0).toLocaleString("id-ID");
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Populasi</p>
          <p className="mt-1 text-2xl font-extrabold text-slate-800">{r(demo.pop_total)}</p>
          <p className="text-[10px] text-slate-400">dalam radius {demo.radius}</p>
        </div>
        <div className="rounded-xl bg-blue-50/60 p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-blue-400">Laki-laki</p>
          <p className="mt-1 text-2xl font-extrabold text-blue-600">{demo.male_pct}%</p>
          <p className="text-[10px] text-blue-400">{r(demo.pop_male)} orang</p>
        </div>
        <div className="rounded-xl bg-pink-50/60 p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-pink-400">Perempuan</p>
          <p className="mt-1 text-2xl font-extrabold text-pink-600">{demo.female_pct}%</p>
          <p className="text-[10px] text-pink-400">{r(demo.pop_female)} orang</p>
        </div>
      </div>
      <div className="space-y-2.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Struktur usia</p>
        {demo.age_groups.map((g, i) => (
          <div key={g.label} className="flex items-center gap-3">
            <span className="w-32 shrink-0 text-[11px] font-medium text-slate-600">{g.label}</span>
            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${g.pct ?? 0}%`, backgroundColor: AGE_COLORS[i % AGE_COLORS.length] }} />
            </div>
            <span className="w-10 shrink-0 text-right text-[11px] font-bold tabular-nums text-slate-700">{g.pct}%</span>
            <span className="w-16 shrink-0 text-right text-[10px] tabular-nums text-slate-400">{r(g.value)}</span>
          </div>
        ))}
      </div>

      {demo.workingage_cohorts?.some((c) => c.value > 0) && (
        <div className="space-y-2.5 pt-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Kelompok usia produktif <span className="font-normal normal-case text-slate-300">· rentang tumpang tindih</span></p>
          {(() => {
            const maxC = Math.max(...demo.workingage_cohorts.map((c) => c.value), 1);
            return demo.workingage_cohorts.map((c, i) => (
              <div key={c.label} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-[11px] font-medium text-slate-600">{c.label}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.value / maxC) * 100}%`, backgroundColor: AGE_COLORS[(i + 3) % AGE_COLORS.length] }} />
                </div>
                <span className="w-16 shrink-0 text-right text-[11px] font-bold tabular-nums text-slate-700">{r(c.value)}</span>
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

function DensityBar({ level }) {
  // level 0..4 (very-low..very-high); -1 unknown
  return (
    <div className="flex gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-1.5 w-3 rounded-sm"
          style={{ backgroundColor: i <= level ? (level >= 3 ? "#0d9488" : level >= 2 ? "#f59e0b" : "#94a3b8") : "#e2e8f0" }} />
      ))}
    </div>
  );
}

// POI density counted from the Places results this report already fetched — no extra
// API call. Google caps each search, so a saturated count is a floor, said plainly
// rather than presented as a census.
function PoiDensity({ density: d }) {
  if (!d?.total) return null;
  const rk = Math.round(((d.radius_m || 0) / 1000) * 10) / 10;
  const plus = d.saturated ? "+" : "";
  const max = Math.max(1, ...(d.by_category || []).map((c) => c.count));
  const cats = d.by_category || [];
  const pct = (n) => Math.round((n / d.total) * 100);
  return (
    <div>
      {/* Hero band — one measure, so a single hue that darkens with magnitude. */}
      <div className="rounded-2xl bg-teal-50 border border-teal-100 px-5 py-4 mb-4">
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span className="text-[44px] font-black text-teal-700 tabular-nums leading-none">
            {d.total.toLocaleString("id-ID")}{plus}
          </span>
          <span className="text-sm font-bold text-slate-600">tempat</span>
          <span className="text-xs text-slate-400">dalam radius {rk} km</span>
        </div>
        {d.per_km2 != null && (
          <p className="text-xs text-slate-500 mt-2">
            ≈ <b className="tabular-nums">{d.per_km2}{plus}</b> per km² · dihitung dari hasil Google Maps
          </p>
        )}
      </div>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.label} className="flex items-center gap-3" title={`${c.label}: ${c.count} tempat (${pct(c.count)}%)`}>
            <span className="w-40 text-xs text-slate-500 shrink-0 truncate">{c.label}</span>
            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-teal-600 transition-[width] duration-500"
                style={{ width: `${(c.count / max) * 100}%`, opacity: 0.45 + 0.55 * (c.count / max) }} />
            </div>
            <span className="w-16 text-right text-xs font-semibold text-slate-600 tabular-nums shrink-0">
              {c.count} <span className="text-slate-400 font-normal">{pct(c.count)}%</span>
            </span>
          </div>
        ))}
      </div>
      {d.saturated && (
        <p className="text-[10px] text-amber-600 mt-3">
          Google membatasi jumlah hasil per pencarian — angka ini batas bawah, jumlah sebenarnya bisa lebih banyak.
        </p>
      )}
    </div>
  );
}

export function NeighbourhoodProfile({ areaProfile, poiDensity }) {
  if (!areaProfile?.length && !poiDensity?.length) return null;
  return (
    <div className="space-y-6">
      {areaProfile?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Karakter Area</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0">
            {areaProfile.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50">
                <span className="text-[12px] text-slate-500">{a.label}</span>
                <span className="text-[12px] font-bold text-slate-700">{a.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {poiDensity?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Kepadatan POI di Kelurahan</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0">
            {poiDensity.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50">
                <span className="text-[12px] text-slate-500 flex-1 min-w-0 truncate">{d.label}</span>
                <DensityBar level={d.level} />
                <span className="text-[11px] font-semibold text-slate-600 w-20 text-right shrink-0">{d.value}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Kepadatan relatif tiap kategori di kelurahan ini.</p>
        </div>
      )}
    </div>
  );
}

// ─── Shared report body ──────────────────────────────────────────────────────────
// The rendered analysis sections, driven purely by a stored `result` object.
// Used by the live Deep Analysis page AND the Report-History version modal, so
// any change to a section shows up in both — one source of truth, no duplication.
// ─── Market Read: quality / saturation / survivability / price (from real data) ───
// ─── PDF export entry point ───────────────────────────────────────────────────────
// "Unduh Laporan PDF": the report's own React components, rendered through the
// browser's print dialog (pages/DeepAnalysisPrint.jsx), so the PDF looks like the
// page. Synchronous up to window.open — a pop-up blocker eats anything later.
// `audit` is the export-log entry; the print page records it when the dialog opens.
// Everything a report needs to be rendered elsewhere — the print page, or a
// shared link's snapshot — resolved from the live page's values.
export function deepAnalysisTicket(args) {
  const { result, profileView, generatedAt, showBonus,
    gridLabel, fullLocation, namaKabkot, subcategory, target_ses, radius, resolution, lat, lng,
    score, idx, verdict, yourFit } = args;
  return {
    kind: "deep-analysis", result, profileView, generatedAt, showBonus,
    view: {
      gridLabel, fullLocation, namaKabkot, subcategory, targetSes: target_ses, radius, resolution, lat, lng,
      score, idx, verdict, yourFit,
      groundLabel: result?.analysis?.on_ground_verdict?.label ?? null,
      // Cover labels, resolved here so the print page needs none of this file's tables.
      suitabilityLabel: idLabel(SUITABILITY[idx]) ?? "-",
      generatedLabel:   fmtGenerated(generatedAt),
    },
    fileName: `Landhep_${gridLabel}_DeepAnalysis`,
  };
}

export function exportDeepAnalysisPDF({ audit, ...args }) {
  openDeepAnalysisPrint({ ...deepAnalysisTicket(args), audit });
}

// The headline pill (hero + PDF cover): the score verdict, qualified by what the
// live map showed. A green RECOMMENDED above a first card that says "perlu
// kehati-hatian" read as a contradiction; now the pill carries both.
export function combinedVerdict(verdict, groundLabel) {
  if (!verdict) return null;
  const g = (groundLabel || "").trim();
  const suffix = g === "Confirms" ? "lapangan menguatkan"
    : g === "Adds Caution" ? "perlu kehati-hatian di lapangan"
    : g === "Contradicts" ? "lapangan bertentangan" : null;
  let tone = verdict === "RECOMMENDED" ? "good" : verdict === "NOT RECOMMENDED" ? "bad" : "warn";
  if (g === "Contradicts" && tone === "good") tone = "warn";
  return { verdict, suffix, tone };
}

const LEVEL_STYLE = {
  good: { pill: "bg-emerald-50 text-emerald-700", num: "text-emerald-600" },
  warn: { pill: "bg-amber-50 text-amber-700", num: "text-amber-600" },
  bad:  { pill: "bg-red-50 text-red-600", num: "text-red-600" },
};
const rp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

function MetricCard({ label, headline, level = "good", verdict, children }) {
  const s = LEVEL_STYLE[level] || LEVEL_STYLE.good;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col">
      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{label}</p>
      {headline && <div className={`text-3xl font-extrabold leading-none mb-1 ${s.num}`}>{headline}</div>}
      {children}
      {verdict && <p className="text-[11px] text-slate-500 leading-snug mt-2">{verdict}</p>}
    </div>
  );
}

export function MarketRead({ market }) {
  if (!market) return null;
  const { competitor_quality: q, saturation: sat, survivability: sv, price_positioning: pp, weakest_competitor: wk } = market;
  if (!q && !sat && !sv && !pp) return null;
  return (
    <div className="grid grid-cols-2 gap-3">
      {q && (
        <MetricCard label="Kualitas kompetitor" headline={`${q.avg_rating}★`} level={q.level} verdict={q.verdict}>
          <p className="text-[10px] text-slate-400">rata-rata rating dari {q.n} rival</p>
        </MetricCard>
      )}
      {sat && (
        <MetricCard label="Kejenuhan pasar" headline={`1 : ${sat.per_resident.toLocaleString("id-ID")}`} level={sat.level} verdict={sat.verdict}>
          <p className="text-[10px] text-slate-400">toko per penduduk · {rivalCount(sat.shops, sat.capped)} dalam radius</p>
        </MetricCard>
      )}
      {sv && (
        <MetricCard label="Keberlangsungan" headline={sv.closed > 0 ? `${sv.closed} tutup` : "Sehat"} level={sv.level} verdict={sv.verdict}>
          <p className="text-[10px] text-slate-400">{sv.operational} beroperasi dari {sv.total} ditemukan</p>
        </MetricCard>
      )}
      {pp && (
        <MetricCard label="Posisi harga" headline={idLabel(pp.modal)} level={pp.level} verdict={pp.verdict}>
          <div className="flex gap-1.5 mt-1 text-[10px] font-semibold">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Hemat {pp.distribution.budget}</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Menengah {pp.distribution.mid}</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Premium {pp.distribution.premium}</span>
          </div>
        </MetricCard>
      )}
      {wk && (
        <div className="col-span-2 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-amber-500 mb-1">Siapa yang bisa dikalahkan</p>
          <p className="text-sm"><span className="font-bold text-slate-800">{wk.name}</span>
            <span className="text-slate-500"> · {wk.rating}★ · {Number(wk.reviews).toLocaleString("id-ID")} ulasan</span></p>
          <p className="text-[11px] text-slate-500 mt-1">{wk.reason}</p>
        </div>
      )}
    </div>
  );
}

// Trade Areas — the catchment turned into a sales projection. Everything is a
// RANGE by design: the capture share is a naive equal split and affordability is
// a two-signal proxy, so a single number would be false precision. Assumptions
// are printed in full rather than hidden behind an info icon — the caps are
// guardrails, and a guardrail the reader cannot see reads as a measurement.
function TradeAreas({ ta }) {
  if (!ta) return null;
  const rp = (n) => `Rp ${Number(n).toLocaleString("id-ID")}`;
  const num = (n) => Number(n).toLocaleString("id-ID");
  const pct = (n) => `${Number(n).toFixed(1)}%`;
  const exp = ta.expectation;

  return (
    <div className="flex flex-col gap-3">
      {ta.confidence === "low" && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-2.5">
          <p className="text-[11px] text-amber-700">{ta.confidence_note}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <MetricCard label="Ukuran pasar" headline={num(ta.market_size)}
          verdict={`Penduduk dalam radius ${ta.radius || "analisis"} · ${rivalCount(ta.competitors, ta.competitors_capped)} rival sejenis ditemukan di radius yang sama`}>
          <p className="text-[10px] text-slate-400">penduduk dalam jangkauan</p>
        </MetricCard>

        <MetricCard label="Pangsa pasar realistis"
          headline={`${pct(ta.target_pct.low)}–${pct(ta.target_pct.high)}`}
          verdict="Perkiraan pangsa Anda dari jangkauan ini, dengan membaginya bersama rival yang sudah ada.">
          <p className="text-[10px] text-slate-400">kasus tengah {pct(ta.target_pct.mid)}</p>
        </MetricCard>

        <MetricCard label="Warga dalam jangkauan Anda"
          headline={`${num(ta.market_segment.low)}–${num(ta.market_segment.high)}`}
          verdict="Ukuran pasar × pangsa realistis — penduduk di wilayah yang bisa Anda menangkan, bukan pelanggan aktif per bulan.">
          <p className="text-[10px] text-slate-400">kasus tengah {num(ta.market_segment.mid)} orang</p>
        </MetricCard>

        {ta.monthly_sales && ta.monthly_customers ? (
          <MetricCard label="Perkiraan omzet bulanan"
            headline={`${rp(ta.monthly_sales.low)} – ${rp(ta.monthly_sales.high)}`}
            verdict={`Volume rival tipikal di sini (~${num(ta.monthly_customers.low)}–${num(ta.monthly_customers.high)} transaksi/bulan) × belanja ${rp(ta.basket_size)} per kunjungan.`}>
            <p className="text-[10px] text-slate-400">kasus tengah {rp(ta.monthly_sales.mid)}</p>
          </MetricCard>
        ) : (
          <MetricCard label="Perkiraan omzet bulanan" headline="—"
            verdict={ta.monthly_sales_note
              || (ta.basket_size != null
                ? "Laporan ini dibuat dengan model omzet lama — perbarui data untuk perkiraan yang mengikuti volume rival di sini."
                : "Tambahkan besar belanja per kunjungan di Kelola Profil untuk memperkirakan omzet.")} />
        )}
      </div>

      {exp && (
        <div className={`rounded-2xl border p-4 ${
          exp.level === "bad"  ? "border-red-200 bg-red-50/60"
          : exp.level === "warn" ? "border-amber-200 bg-amber-50/60"
          : "border-emerald-200 bg-emerald-50/60"}`}>
          <p className={`text-[9px] font-bold uppercase tracking-widest mb-1 ${
            exp.level === "bad" ? "text-red-500" : exp.level === "warn" ? "text-amber-500" : "text-emerald-600"}`}>
            Ekspektasi Anda
          </p>
          <p className="text-[11px] text-slate-600">{exp.verdict}</p>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Dasar perhitungan ini</p>
        <ul className="flex flex-col gap-1">
          {ta.assumptions.map((a, i) => (
            <li key={i} className="text-[11px] leading-relaxed text-slate-500 flex gap-2">
              <span className="text-slate-300">·</span><span>{a}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Purchasing Power — per-kelurahan property prices (real listings, via web search)
// and a derived household-income estimate. Prices are facts with a source; income
// is one assumption on top, shown as a range and labelled as an estimate. Every
// caveat is printed, never hidden behind an icon.
function BuyingPowerIndex({ bp }) {
  if (!bp?.index) return null;
  const v = bp.index;
  const color = v >= 115 ? "#059669" : v <= 85 ? "#dc2626" : "#0d9488";
  const low = bp.confidence === "low";
  // Marker position on a 40–200 scale (100 = area average).
  const pos = Math.max(0, Math.min(100, ((v - 40) / (200 - 40)) * 100));
  const midPos = ((100 - 40) / (200 - 40)) * 100;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Indeks Daya Beli Lokal</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-extrabold tabular-nums" style={{ color }}>{v}</span>
            <span className="text-xs text-slate-400">vs 100 · area ~{bp.baseline_km} km</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1 capitalize">{bp.label}</p>
        </div>
        {low && <span className="shrink-0 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider px-2 py-1">Keyakinan rendah</span>}
      </div>

      {/* Scale 40–200, marker at index, tick at 100 */}
      <div className="relative mt-3 h-1.5 rounded-full bg-gradient-to-r from-red-200 via-slate-200 to-emerald-200">
        <div className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-slate-400" style={{ left: `${midPos}%` }} />
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow" style={{ left: `${pos}%`, backgroundColor: color }} />
      </div>
      <div className="flex justify-between text-[9px] text-slate-300 mt-1"><span>lebih rendah</span><span>rata-rata area</span><span>lebih tinggi</span></div>

      {/* #4 transparency — what pushed it up / down */}
      {(bp.drivers_up?.length > 0 || bp.drivers_down?.length > 0) && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 mb-1">Faktor pendorong</p>
            <div className="flex flex-wrap gap-1">
              {(bp.drivers_up || []).map((d) => (
                <span key={d.category} className="rounded-md bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">{d.label} · {d.count}</span>
              ))}
              {!bp.drivers_up?.length && <span className="text-[10px] text-slate-300">—</span>}
            </div>
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Faktor penekan</p>
            <div className="flex flex-wrap gap-1">
              {(bp.drivers_down || []).map((d) => (
                <span key={d.category} className="rounded-md bg-slate-50 border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-500">{d.label} · {d.count}</span>
              ))}
              {!bp.drivers_down?.length && <span className="text-[10px] text-slate-300">—</span>}
            </div>
          </div>
        </div>
      )}
      {bp.productive_age_share != null && (
        <p className="text-[10px] text-slate-400 mt-2">Proporsi usia produktif dalam jangkauan: {Math.round(bp.productive_age_share * 100)}%.</p>
      )}
      <ul className="mt-3 flex flex-col gap-1">
        {(bp.assumptions || []).map((a, i) => (
          <li key={i} className="text-[10px] leading-relaxed text-slate-400 flex gap-1.5"><span className="text-slate-300">·</span><span>{a}</span></li>
        ))}
      </ul>
    </div>
  );
}

function PurchasingPower({ pp, bp, premiumBrands = [] }) {
  if (!pp && !premiumBrands.length && !bp?.index) return null;
  const rp = (n) => (n == null ? "—" : `Rp ${Number(n).toLocaleString("id-ID")}`);
  // Reports generated before the rename still carry the old key; read both so a
  // paid report does not quietly lose the card it was generated with.
  const inc = pp?.affordability_threshold_monthly ?? pp?.household_income_monthly;
  const low = pp?.confidence === "low";

  return (
    <div className="flex flex-col gap-3">
      <BuyingPowerIndex bp={bp} />

      {pp && low && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-2.5">
          <p className="text-[11px] text-amber-700">
            Keyakinan rendah — hasil pencarian web tidak konsisten atau data listing di sini tipis. Anggap sebagai perkiraan kasar.
          </p>
        </div>
      )}

      {pp && (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard label="Harga tanah umum"
            headline={pp.land_price_typical_idr_m2 ? `${rp(pp.land_price_typical_idr_m2)}/m²` : "—"}
            verdict={`Rentang ${rp(pp.land_price_min_idr_m2)} – ${rp(pp.land_price_max_idr_m2)} per m² di kelurahan ini.`}>
            <p className="text-[10px] text-slate-400">dari listing daring</p>
          </MetricCard>

          <MetricCard label="Harga rumah umum"
            headline={rp(pp.house_price_typical_idr)}
            verdict={pp.house_price_min_idr
              ? `Rentang ${rp(pp.house_price_min_idr)} – ${rp(pp.house_price_max_idr)}.`
              : "Rumah listing umum di kelurahan ini."}>
            <p className="text-[10px] text-slate-400">median listing</p>
          </MetricCard>
        </div>
      )}

      {inc && (
        <MetricCard label="Tiket masuk properti"
          headline={`${rp(inc.low)} – ${rp(inc.high)} / bulan`}
          verdict="Penghasilan yang dibutuhkan untuk MEMBELI rumah tipikal yang dijual di sini — bukan penghasilan warga pada umumnya. Mayoritas penghuni sudah memiliki rumahnya sejak lama atau mewarisinya.">
          <p className="text-[10px] text-slate-400">turunan · asumsi rumah ≈ 5× penghasilan tahunan rumah tangga</p>
          {pp?.listing_bias && (
            <p className="text-[10px] text-amber-600 mt-1.5 leading-relaxed">
              ⚠️ Kelurahan ini tergolong kelas pendapatan <b>{idLabel(pp.income_class)}</b>, jauh di bawah angka di atas —
              pertanda listing yang ditemukan condong ke segmen atas pasar lokal. Baca ini sebagai kelas pasar properti,
              bukan kondisi warganya.
            </p>
          )}
        </MetricCard>
      )}

      {premiumBrands.length > 0 && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-violet-500 mb-2">Merek premium terdekat · sinyal kelas atas</p>
          <div className="flex flex-wrap gap-1.5">
            {premiumBrands.map((b) => (
              <span key={b.brand} className="rounded-full bg-white border border-violet-200 px-2.5 py-1 text-[11px] font-medium text-violet-700">
                {b.brand} <span className="text-violet-400">· {b.count} · {b.nearest}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {pp && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Dasar perhitungan ini</p>
          <ul className="flex flex-col gap-1">
            {(pp.assumptions || []).map((a, i) => (
              <li key={i} className="text-[11px] leading-relaxed text-slate-500 flex gap-2">
                <span className="text-slate-300">·</span><span>{a}</span>
              </li>
            ))}
          </ul>
          {pp.source_note && (
            <p className="mt-2 text-[10px] text-slate-400">Sumber: {pp.source_note}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function Throughput({ throughput }) {
  const t = throughput;
  if (!t || !t.per_competitor?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md bg-amber-100 text-amber-700">◑ Estimasi model</span>
        <span className="text-[11px] text-slate-400">rentang, bukan angka pasti — makin akurat seiring pemindaian ulang</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="text-[9px] uppercase tracking-wide text-slate-400 text-right">
              <th className="text-left pb-2 font-bold">Kompetitor</th>
              <th className="pb-2 font-bold">Ulasan</th>
              <th className="pb-2 font-bold">Est. pengunjung/hari</th>
              <th className="pb-2 font-bold">Est. pendapatan/hari</th>
              <th className="pb-2 font-bold">Keyakinan</th>
            </tr>
          </thead>
          <tbody>
            {t.per_competitor.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 text-right">
                <td className="text-left py-2 font-semibold text-slate-700 truncate max-w-[160px]">{r.name}</td>
                <td className="py-2 text-slate-500">{Number(r.reviews).toLocaleString("id-ID")}</td>
                <td className="py-2 font-bold text-teal-700">{r.customers_day_low}–{r.customers_day_high}</td>
                <td className="py-2 font-bold text-slate-800">{rp(r.revenue_day_low)}–{rp(r.revenue_day_high)}</td>
                <td className="py-2"><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${r.confidence === "med" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>{r.confidence.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 flex items-baseline gap-2">
        <span className="text-lg font-extrabold text-slate-800">≈ {t.cluster_customers_low.toLocaleString("id-ID")}–{t.cluster_customers_high.toLocaleString("id-ID")}</span>
        <span className="text-[11px] text-slate-500">pengunjung/hari di seluruh klaster — pasar yang akan Anda perebutkan</span>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        Asumsi: {t.assumptions.reviews_per_customer} pelanggan per ulasan · {ticketLabel(t.assumptions)}. {t.assumptions.note}
      </p>
    </div>
  );
}

function VoiceOfCustomer({ voc }) {
  if (!voc || (!voc.praise?.length && !voc.complaints?.length)) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 mb-2">✓ Konsisten dipuji</p>
          <div className="flex flex-wrap gap-1.5">
            {(voc.praise || []).map((p, i) => <span key={i} className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700">{p}</span>)}
          </div>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-red-500 mb-2">✕ Keluhan umum</p>
          <div className="flex flex-wrap gap-1.5">
            {(voc.complaints || []).map((p, i) => <span key={i} className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-red-50 text-red-600">{p}</span>)}
          </div>
        </div>
      </div>
      {voc.opportunity && (
        <div className="rounded-xl bg-teal-50/70 border border-teal-200 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-teal-700 mb-1">Peluang Anda</p>
          <p className="text-sm font-semibold text-slate-800">{voc.opportunity}</p>
        </div>
      )}
    </div>
  );
}

const RHY_LEVEL = { match: "bg-emerald-50 text-emerald-700 border-emerald-200", partial: "bg-amber-50 text-amber-700 border-amber-200", mismatch: "bg-red-50 text-red-600 border-red-200" };
// Fallback for when BestTime foot-traffic is off/unavailable: an INFERRED busy-hours
// pattern from the Gemini reasoning call (no extra API cost). Clearly labelled as an
// estimate so it is never mistaken for the measured Area Rhythm.
function BusyHoursEstimate({ est }) {
  if (!est) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 px-4 py-2.5">
        <p className="text-[11px] text-amber-700">
          Pola perkiraan — diperkirakan dari jenis usaha dan lingkungan sekitar titik ini,
          bukan footprint yang terukur. {est.basis ? `Dasar: ${est.basis}.` : ""}
        </p>
      </div>
      {est.pattern && (
        <p className="text-sm font-semibold text-slate-700">{est.pattern}</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 mb-2">Kemungkinan puncak</p>
          <div className="flex flex-col gap-1.5">
            {(est.peaks || []).map((p, i) => (
              <span key={i} className="text-[12px] font-medium text-slate-700">↑ {p}</span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Kemungkinan sepi</p>
          <div className="flex flex-col gap-1.5">
            {(est.quiet || []).map((q, i) => (
              <span key={i} className="text-[12px] font-medium text-slate-500">↓ {q}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AreaRhythm({ rhythm }) {
  if (!rhythm || !rhythm.window?.length) return null;
  const max = Math.max(...rhythm.window, 1);
  const barColor = (v) => v >= 85 ? "#b7893f" : v >= 65 ? "#0f766e" : v >= 45 ? "#0d9488aa" : "#0d948855";
  return (
    <div>
      <div className="flex items-end gap-[3px] h-24">
        {rhythm.window.map((v, i) => (
          <div key={i} className="flex-1 rounded-t" style={{ height: `${Math.max(4, v / max * 100)}%`, background: barColor(v) }} title={`${rhythm.hours[i]}:00 · ${v}`} />
        ))}
      </div>
      <div className="flex gap-[3px] mt-1">
        {rhythm.hours.map((h, i) => (
          <span key={i} className="flex-1 text-center text-[8px] text-slate-400 font-semibold">{[6, 9, 12, 15, 18, 21].includes(h) ? h : ""}</span>
        ))}
      </div>
      {rhythm.fit?.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          {rhythm.fit.map((f, i) => (
            <div key={i} className={`rounded-xl border p-3 ${RHY_LEVEL[f.verdict] || RHY_LEVEL.partial}`}>
              <p className="text-[10px] font-bold uppercase tracking-wide">{f.verdict === "match" ? "✓" : f.verdict === "mismatch" ? "✕" : "◑"} {f.window}</p>
              <p className="text-[11px] mt-0.5">Keramaian area di jam ini: <b>{f.avg_busyness}</b>/100</p>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Keramaian hari biasa dari {rhythm.venues_used} tempat terdekat · API footprint.</p>
    </div>
  );
}

// `printMode` (pages/DeepAnalysisPrint.jsx): maps become snapshots, tabs unfold,
// nothing else changes — the PDF is this component.
export function DeepAnalysisReport({ result, targetSes = "all", radius, profileView, showBonus = true, printMode = false }) {
  const rad     = radius ?? result?.radius ?? "3km";
  // Older reports can carry competitors from other continents (see lib/geo.js);
  // every section below reads this filtered copy, so map, table and PDF agree.
  const analysis = withinRadius(result?.analysis ?? null, rad, { lat: result?.lat, lng: result?.lng });
  if (!analysis) return null;
  const fitView = buildFitView(result, analysis, targetSes);

  const bonusDemo = showBonus && result?.db_facts?.demographics;
  const bonusNbhd = showBonus && (result?.db_facts?.area_profile?.length > 0 || result?.db_facts?.poi_density?.length > 0);

  // Sections are grouped into a decision funnel so the report reads as one
  // narrative, not a pile of cards: Context+Answer → does it fit YOU → the AREA →
  // the COMPETITION → the OPPORTUNITY (climax) → timing → reference. Kept identical
  // in the PDF (the print page renders this same component) so both tell the same story.
  return (
    <div className="space-y-5">
      {/* ── Context + the answer ─────────────────────────────────────── */}
      {profileView?.hasAnything && (
        <Section title="Profil Bisnis"
          subtitle="Profil onboarding yang menjadi dasar personalisasi analisis ini"
          icon={Briefcase}>
          <BusinessProfile view={profileView} />
        </Section>
      )}

      <GroundScorecard analysis={analysis} />
      <OnGroundVerdict verdict={analysis.on_ground_verdict} />

      {((analysis.competitors || []).some((c) => c.lat != null)
        || (analysis.ecosystem || []).some((e) => (e.places || []).some((p) => p.lat != null))) && (
        <Section title="Peta Lokasi"
          subtitle="Setiap tempat di bawah adalah lokasi Google Maps nyata di sekitar titik yang Anda klik"
          icon={Compass}>
          <ReportMap lat={result?.lat} lng={result?.lng}
            competitors={analysis.competitors} ecosystem={analysis.ecosystem}
            staticImage={printMode} />
        </Section>
      )}

      {fitView?.groups?.length > 0 && (
        <Section title="Profile Fit — Apakah lokasi ini cocok untuk bisnis ANDA?"
          subtitle="Kebutuhan onboarding Anda dicocokkan dengan kondisi nyata di sini"
          icon={ListChecks} long>
          <ProfileFit fit={fitView} />
        </Section>
      )}

      {/* ── The Area ─────────────────────────────────────────────────── */}
      <GroupHeader label="Area" hint="Siapa yang tinggal di sini dan berapa biayanya" />

      <Section title="Analisis Area & Properti" subtitle="Karakter area, estimasi sewa, dan posisi pasar" icon={Building2}>
        <AreaProperty classification={analysis.area_classification} rent={analysis.rent_estimate} />
      </Section>

      {SHOW_DAYA_BELI && (analysis.purchasing_power || analysis.buying_power?.index || analysis.premium_brands?.length > 0) && (
        <Section title="Daya Beli"
          subtitle="Indeks daya beli lokal, harga properti, dan sinyal merek premium untuk area ini" icon={Target}>
          <PurchasingPower pp={analysis.purchasing_power} bp={analysis.buying_power} premiumBrands={analysis.premium_brands || []} />
        </Section>
      )}

      <Section title="Ekosistem Pendukung" subtitle="POI pendukung di sekitar titik ini — tab per kategori" icon={Building2} long>
        <Ecosystem ecosystem={analysis.ecosystem} radius={rad} printMode={printMode} />
      </Section>

      {SHOW_POI_DENSITY_GOOGLE && analysis.poi_density_google?.total > 0 && (
        <Section title="Kepadatan POI di Sekitar Titik"
          subtitle="Seberapa padat tempat usaha & fasilitas dalam radius pindai — dihitung dari hasil Google Maps"
          icon={MapPin}>
          <PoiDensity density={analysis.poi_density_google} />
        </Section>
      )}

      {/* ── The Competition ──────────────────────────────────────────── */}
      <GroupHeader label="Kompetisi" hint="Siapa lawan Anda, dan bagaimana kinerja mereka" />

      <Section title="Analisis Kompetitor Terdekat" subtitle={competitorSubtitle(analysis, rad)} icon={Users} long>
        <CompetitorAnalysis
          execSummary={analysis.competition_exec_summary}
          competitors={analysis.competitors}
          actionPlan={analysis.action_plan}
          center={{ lat: result?.lat, lng: result?.lng }}
          printMode={printMode}
        />
      </Section>

      {analysis.market && (analysis.market.competitor_quality || analysis.market.saturation
        || analysis.market.survivability || analysis.market.price_positioning) && (
        <Section title="Pembacaan Pasar" subtitle="Apa arti kondisi kompetitor bagi rencana membuka usaha di sini" icon={Target}>
          <MarketRead market={analysis.market} />
        </Section>
      )}

      {analysis.market?.throughput?.per_competitor?.length > 0 && (
        <Section title="Estimasi Volume Kompetitor"
          subtitle="Perkiraan seberapa ramai — dan seberapa besar — penjualan rival · dimodelkan dari volume ulasan × harga" icon={Users}>
          <Throughput throughput={analysis.market.throughput} />
        </Section>
      )}

      {analysis.voice_of_customer && (
        <Section title="Suara Pelanggan"
          subtitle="Apa yang dipuji dan dikeluhkan orang tentang kompetitor terdekat — dari ulasan asli mereka" icon={Star}>
          <VoiceOfCustomer voc={analysis.voice_of_customer} />
        </Section>
      )}

      <Section title="Kecocokan Benchmark" subtitle="Merek acuan Anda yang ditemukan di sekitar titik ini — per sinyal" icon={Target} long>
        <BenchmarkMatch matches={analysis.benchmark_matches} />
      </Section>

      {/* ── The Opportunity (climax) ─────────────────────────────────── */}
      {analysis.trade_areas && (
        <>
          <GroupHeader label="Peluang" hint="Berapa perkiraan nilai lokasi ini" />
          <Section title="Area Perdagangan"
            subtitle="Perkiraan nilai jangkauan ini bagi Anda · sebuah rentang, bukan janji pasti" icon={Users}>
            <TradeAreas ta={analysis.trade_areas} />
          </Section>
        </>
      )}

      {/* ── Timing ───────────────────────────────────────────────────── */}
      {(analysis.area_rhythm?.window?.length > 0 || analysis.busy_hours_estimate) && (
        <>
          <GroupHeader label="Waktu" hint="Kapan area ini ramai" />
          {analysis.area_rhythm?.window?.length > 0 ? (
            <Section title="Ritme Area vs Jam Operasional Anda"
              subtitle="Kapan area ini benar-benar ramai, dicocokkan dengan rencana jam operasional Anda" icon={Compass}>
              <AreaRhythm rhythm={analysis.area_rhythm} />
            </Section>
          ) : (
            <Section title="Perkiraan Jam Ramai"
              subtitle="Diperkirakan dari jenis usaha + lingkungan sekitar — bukan footprint terukur" icon={Compass}>
              <BusyHoursEstimate est={analysis.busy_hours_estimate} />
            </Section>
          )}
        </>
      )}

      {/* ── Reference (bonus DB-sourced) ─────────────────────────────── */}
      {(bonusDemo || bonusNbhd) && <GroupHeader label="Referensi" hint="Data pendukung dari dataset nasional kami" />}
      {bonusDemo && (
        <Section title={`Populasi dalam radius ${result.db_facts.demographics.radius}`}
          subtitle="Populasi jangkauan berdasarkan gender dan tahap hidup — dari data sensus" icon={Users}>
          <PopulationByGroup demo={result.db_facts.demographics} />
        </Section>
      )}
      {bonusNbhd && (
        <Section title="Profil Kelurahan"
          subtitle="Karakter area dan kepadatan titik minat — dari dataset spasial kami" icon={Layers}>
          <NeighbourhoodProfile
            areaProfile={result.db_facts.area_profile}
            poiDensity={result.db_facts.poi_density}
          />
        </Section>
      )}
    </div>
  );
}

// The table shows the nearest N; the market metrics count every rival found in the
// radius (capped by Google at 20). Say both, so "8 rows" is not read as "8 rivals".
function competitorSubtitle(analysis, rad) {
  const shown = (analysis?.competitors || []).length;
  const ta = analysis?.trade_areas, sat = analysis?.market?.saturation;
  const total = ta?.competitors ?? sat?.shops ?? null;
  const capped = ta?.competitors_capped ?? sat?.capped;
  const base = `Kompetitor dan potensi dampaknya dalam radius ${rad} · via Google Maps`;
  if (!total || total <= shown) return base;
  return `${shown} terdekat dari ${rivalCount(total, capped)} yang ditemukan dalam radius ${rad} · via Google Maps`;
}

// Thematic divider that groups the report sections into a decision funnel.
// `ai` marks a group whose narrative cards were written by the model from the
// data shown next to them — said ONCE here, not as a badge on every card: the
// per-card badges made the whole report read as machine output.
export function GroupHeader({ label, hint, ai = false }) {
  return (
    <div className="da-group flex items-center gap-3 pt-4 pb-0.5">
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{label}</span>
      {hint && <span className="text-[11px] text-slate-300 whitespace-nowrap hidden sm:inline">{hint}</span>}
      <span className="flex-1 h-px bg-slate-200" />
      {ai && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap" title="Narasi di bagian ini disusun AI dari data yang tampil; angka dan peringkat dihitung dari data.">✦ narasi AI</span>}
    </div>
  );
}

// ─── Shared hero ─────────────────────────────────────────────────────────────────
// The dark score header (Site Score + Your Fit gauges, verdict, grid identity).
// Shared by the live page and the history modal so both show an identical header.
// Pass `onBack` to show the "Back to Comparison" top bar (page only).
export function DeepAnalysisHero({
  gridLabel, fullLocation, result, resolution, lat, lng,
  verdict, score, idx, yourFit, subcategory, targetSes, radius, generatedAt, onBack,
}) {
  return (
    <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f172a 0%, #134e4a 42%, #0f766e 100%)" }}>
      <div className="relative max-w-6xl mx-auto px-8 py-10">
        {onBack && (
          <div className="flex items-center justify-between mb-8">
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-white/60 hover:text-white/80 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Kembali ke Perbandingan
            </button>
            <div className="flex items-center gap-2 bg-green-500/15 border border-green-500/30 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3 h-3 text-green-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-green-400">Analisis Mendalam · Peta Langsung</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-10 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-3.5 h-3.5 text-white/70" />
              <span className="text-xs text-white/70">{fullLocation || "Lokasi tidak diketahui"}</span>
            </div>
            <p className="text-4xl font-extrabold font-mono text-white tracking-widest mb-2 leading-none">{gridLabel}</p>

            {/* Grid identity — type · bounding box · clicked point */}
            <div className="mb-4 space-y-0.5 font-mono text-[10px] text-white/50">
              <p><span className="text-white/40">Tipe Grid:</span> Sel {(result?.grid_meta?.resolution ?? resolution) === "100m" ? "100 m" : "1 km"}</p>
              {(() => {
                const b = result?.grid_meta?.bbox;
                return b ? (
                  <p><span className="text-white/40">Batas Wilayah:</span> {co(b.min_lat)}, {co(b.min_lng)} → {co(b.max_lat)}, {co(b.max_lng)}</p>
                ) : null;
              })()}
              <p><span className="text-white/40">Titik yang Diklik:</span> {co(result?.grid_meta?.clicked?.lat ?? lat)}, {co(result?.grid_meta?.clicked?.lng ?? lng)}</p>
            </div>

            {verdict && <VerdictBadge verdict={verdict} ground={result?.analysis?.on_ground_verdict?.label} />}
            {subcategory && (
              <p className="mt-3 text-[11px] text-white/60">
                {subcategory} · target {idLabel(String(targetSes || "all"))} · radius analisis {radius}
              </p>
            )}
            {generatedAt && (
              <p className="mt-1.5 text-[11px] text-white/45">
                Dibuat: {fmtGenerated(generatedAt)}
              </p>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-8 rounded-2xl bg-white/5 border border-white/10 px-7 py-5">
            <div className="flex flex-col items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Skor Lokasi</span>
              <ScoreGauge score={score} idx={idx} />
            </div>
            {yourFit != null && (
              <>
                <div className="w-px self-stretch bg-white/10" />
                <div className="flex flex-col items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Kecocokan Anda</span>
                  <FitGauge value={yourFit} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Build the "Business Profile" recap view from the current on-device profile.
export function currentProfileView(ctx) {
  return buildProfileView(loadProfile(), ctx);
}

// Derive every value the hero / report / PDF need from a saved zone + its stored
// result snapshot. Lets the history modal render exactly like the live page from
// one place — no logic duplicated across the two callers.
export function deriveDeepAnalysisView(zone, result) {
  const analysis    = result?.analysis ?? null;
  const score       = Math.floor(zone?.score ?? zone?.siteScore ?? 0);
  const idx         = tierIdxOf(zone, score);
  const subcategory = zone?.subcategory ?? null;
  const targetSes   = zone?.target_ses ?? "all";
  const resolution  = zone?.resolution ?? (result?.grid_meta?.resolution ?? "1km");
  const fitView     = buildFitView(result, analysis, targetSes);
  const yourFit     = computeYourFit(fitView?.summary);
  const namaKabkot  = zone?.namaKabkot ?? null;
  const fullLocation = [zone?.namaKelurahan, zone?.namaKecamatan ? `Kec. ${zone.namaKecamatan}` : null, namaKabkot, zone?.namaProvinsi]
    .filter(Boolean).join(" · ") || namaKabkot;
  return {
    analysis, score, idx, subcategory, targetSes, resolution, fitView, yourFit,
    namaKabkot, fullLocation,
    verdict:      zone?.gridId ? computeVerdict(score) : null,
    gridLabel:    zone?.gridId ? `grid-${zone.gridId}` : null,
    lat:          zone?.lat ?? null,
    lng:          zone?.lng ?? null,
    profileView:  currentProfileView({ subcategory, resolution }),
  };
}

// Same as deriveDeepAnalysisView, but for a Branch Radar store: the label is the
// store name and the site score comes from a point-score lookup (not a grid zone).
export function deriveStoreDeepAnalysisView({ store, result, score, subcategory, targetSes = "all" }) {
  const analysis = result?.analysis ?? null;
  const idx      = scoreBucket(score ?? 0);
  const fitView  = buildFitView(result, analysis, targetSes);
  const yourFit  = computeYourFit(fitView?.summary);
  const fullLocation = [store.namaKelurahan, store.namaKecamatan ? `Kec. ${store.namaKecamatan}` : null, store.namaKabkot, store.namaProvinsi]
    .filter(Boolean).join(" · ") || store.namaKabkot;
  return {
    analysis, fitView, yourFit,
    score: score ?? 0, idx,
    verdict:     score != null ? computeVerdict(score) : null,
    gridLabel:   store.name,
    fullLocation,
    namaKabkot:  store.namaKabkot ?? null,
    subcategory, targetSes,
    resolution:  "100m",
    lat: store.lat, lng: store.lng,
    profileView: currentProfileView({ subcategory, resolution: "100m" }),
  };
}

// ─── Main ────────────────────────────────────────────────────────────────────────

export default function DeepAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ownedZones, ownedIds, removeZone, clearZones, refresh, loaded } = useOwnedGrids();

  // The owned-grids context loads once at app boot; a zone created afterwards
  // (in another tab/session, or right after unlocking) would leave this page
  // stale while Report History — which fetches fresh — shows it. Re-sync on
  // mount so "My Grids" always mirrors the DB.
  useEffect(() => { refresh(); }, [refresh]);

  // A grid opened explicitly from Comparison arrives via navigation state.
  const navCell = location.state?.cell || null;
  const [activeZone, setActiveZone] = useState(() => {
    if (navCell) return navCell;
    try {
      localStorage.removeItem("landhep_pro_deep_zone");   // legacy shared key
      return JSON.parse(localStorage.getItem(DEEP_ZONE_KEY()) || "null");
    } catch { return null; }
  });
  // Pin it: its owned-zone save may still be in flight, so the reconcile below
  // must not swap it out for ownedZones[0] during that race.
  const pinnedId = useRef(navCell?.gridId ?? null);
  // Why a paid run may start. "nav": the grid arrived from a cost-confirmed action
  // (Comparison / Skenario hand over a one-shot token). "confirmed": the user just
  // accepted a quote here. null: opening, reloading, picking in the sidebar — and
  // none of those may ever spend money. The token is consumed on read, so a reload
  // of the same URL (history state survives reloads) does not count as intent.
  const liveIntent = useRef((() => {
    try {
      const tok = sessionStorage.getItem("landhep_da_intent");
      sessionStorage.removeItem("landhep_da_intent");
      return navCell && tok && String(tok) === String(navCell.gridId) ? "nav" : null;
    } catch { return null; }
  })());
  const [runNonce, setRunNonce] = useState(0);     // re-runs the fetch for a same-radius confirmed run
  const [needsRun, setNeedsRun] = useState(false); // nothing stored and no intent → ask, don't charge
  const cacheRef      = useRef(null);   // last-seen AI cache blob → know which radii are free
  // Switching to an uncached radius runs a NEW paid report — confirm the cost first.
  const [radiusConfirm, setRadiusConfirm] = useState(null);   // target radius pending confirmation
  const [radiusQuote, setRadiusQuote]     = useState(null);
  const [radiusQuoteLoading, setRadiusQuoteLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [radius, setRadius] = useState("3km");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen,   setShareOpen]   = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);   // ms epoch of current result
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [refreshing,  setRefreshing]  = useState(false);
  const [scanning,    setScanning]    = useState(false);  // true ONLY during the billed live Gemini call
  const [refreshGateDays, setRefreshGateDays] = useState(30);  // admin-set; from billing-status
  useEffect(() => {
    getBillingStatus()
      .then((s) => { if (s?.refresh_gate_days != null) setRefreshGateDays(Number(s.refresh_gate_days)); })
      .catch(() => {});
  }, []);

  // Reconcile the active zone with what's actually owned. Runs only after the
  // owned list has loaded, so we never drop a valid zone during the first fetch.
  // Without this, a zone hydrated from localStorage keeps rendering even after
  // it was deleted or all grids were cleared (it drifts out of ownedZones).
  useEffect(() => {
    if (!loaded) return;
    // Never auto-drop a grid explicitly opened from Comparison this session.
    if (activeZone && activeZone.gridId === pinnedId.current) return;
    if (activeZone && !ownedZones.some((z) => z.gridId === activeZone.gridId)) {
      setActiveZone(ownedZones[0] ?? null);
    } else if (!activeZone && ownedZones.length > 0) {
      setActiveZone(ownedZones[0]);
    }
  }, [ownedZones, loaded]);

  useEffect(() => {
    if (activeZone) localStorage.setItem(DEEP_ZONE_KEY(), JSON.stringify(activeZone));
    else localStorage.removeItem(DEEP_ZONE_KEY());
  }, [activeZone?.gridId]);

  // While a billed Gemini call is running, warn before the tab is closed/reloaded
  // so the user doesn't waste the (paid) analysis mid-flight.
  const busy = scanning || refreshing;
  useEffect(() => {
    if (!busy) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  function handleRemoveZone(gridId) {
    removeZone(gridId);
    if (activeZone?.gridId === gridId) {
      setActiveZone(ownedZones.find((z) => z.gridId !== gridId) ?? null);
    }
  }

  const profile = loadProfile();
  const zone    = activeZone;

  // DB-listed zones expose `siteScore`; the rich zone from Comparison exposes `score`.
  const gridId       = zone?.gridId ?? null;
  const score        = Math.floor(zone?.score ?? zone?.siteScore ?? 0);
  const lat          = zone?.lat ?? null;
  const lng          = zone?.lng ?? null;
  const namaKabkot   = zone?.namaKabkot ?? profile?.city?.nama_kabkot ?? null;
  const namaProvinsi = zone?.namaProvinsi ?? profile?.city?.nama_provinsi ?? null;
  const subcategory  = zone?.subcategory || profile?.bizSubtype || null;   // ||: an empty string must fall through too
  const target_ses   = zone?.target_ses ?? profile?.target_ses ?? "all";
  const benchmarks   = profile?.benchmarks ?? [];
  const resolution   = zone?.resolution ?? (profile?.gridResolution === "100m" ? "100m" : "1km");

  // Typed-report tabs (Klasik + the two Skenario reports) for the same grid.
  const [reportTab, setReportTab] = useState("klasik");
  // Sidebar "Grid Saya" category tabs: which owned grids to list (by focus).
  const [listTab, setListTab] = useState("klasik");
  const [potensiGrids, setPotensiGrids] = useState({ potensi_bisnis: [], potensi_iklan: [] });
  useEffect(() => {
    // grid_id is numeric in this endpoint; the rest of the page carries strings
    // (ownedIds, accessGranted), so normalise here or an owned Potensi grid is
    // treated as unowned the moment it becomes the active zone.
    getOwnedPotensiGrids().then((d) => {
      if (!d?.grids) return;
      const fix = (l) => (l || []).map((g) => ({ ...g, gridId: String(g.gridId) }));
      setPotensiGrids({ potensi_bisnis: fix(d.grids.potensi_bisnis), potensi_iklan: fix(d.grids.potensi_iklan) });
    }).catch(() => {});
  }, []);
  const gridCtx = {
    grid_id: gridId, lat, lng,
    region: {
      kelurahan: zone?.namaKelurahan, kecamatan: zone?.namaKecamatan,
      kabkot: namaKabkot, provinsi: namaProvinsi,
      kode_kelurahan: zone?.kode_kelurahan, kode_kabkot: zone?.kode_kabkot,
    },
  };

  // Onboarding profile recap shown as the report's first section (+ in the PDF).
  const profileView = buildProfileView(profile, { subcategory, resolution });

  // Profile slice that drives Profile Fit (also stored as the log snapshot).
  // Single whitelist shared with Branch Radar (see buildProfileForFit) — it also
  // feeds configForGate below, so changing e.g. a basket size correctly busts the
  // cached result.
  const profileForFit = buildProfileForFit(profile, target_ses);

  // Refresh gate (admin-set days) computed from the current result's generated time.
  // The backend enforces the real gate per config+radius fingerprint; this only
  // drives the button state, so a value of 0 days means "always refreshable".
  const REFRESH_GAP_MS = refreshGateDays * 24 * 60 * 60 * 1000;
  const nextRefreshAt  = generatedAt ? generatedAt + REFRESH_GAP_MS : null;
  const canRefresh     = generatedAt != null && (REFRESH_GAP_MS <= 0 || Date.now() >= nextRefreshAt);

  const accessGranted = !gridId || ownedIds.includes(gridId);
  const idx     = tierIdxOf(zone, score);
  const verdict = gridId ? computeVerdict(score) : null;

  const gridLabel = gridId ? `grid-${gridId}` : null;
  const fullLocation = [zone?.namaKelurahan, zone?.namaKecamatan ? `Kec. ${zone.namaKecamatan}` : null, namaKabkot, namaProvinsi]
    .filter(Boolean).join(" · ") || namaKabkot;

  // Build the request payload for a live generation.
  function buildRequest() {
    return {
      grid_id: gridId, lat, lng, score,
      // `radius` = the Google-search radius (drives API cost, section "Nearby
      // competitors / ecosystem"). `pop_radius` = the user's Population Catchment
      // from the profile — DB-only, drives the "Population within X km" section.
      subtype: subcategory, target_ses, radius, pop_radius: profile?.populationCatchment ?? "5km", benchmarks,
      area: namaKabkot,
      kode_kelurahan: zone?.kode_kelurahan ?? null,
      resolution,
      profile: profileForFit,
      location_info: {
        kelurahan: zone?.namaKelurahan ?? null,
        kecamatan: zone?.namaKecamatan ?? null,
        kabkot:    namaKabkot,
        provinsi:  namaProvinsi,
      },
    };
  }

  // Every setting that changes the analysis result — the backend hashes this into
  // a config fingerprint that keys the refresh gate. Anything omitted here would
  // let a changed setting share a gate with the old one, so keep it exhaustive.
  const configForGate = {
    subtype:       subcategory,
    target_ses,
    resolution,
    pop_radius:    profile?.populationCatchment ?? "5km",
    bizType:       profile?.bizType,
    benchmarks:    (benchmarks || []).map((b) => `${b.name}:${b.type}`),
    profile:       profileForFit,
  };

  // Persist a fresh result + log it (the audit trail / refresh metadata).
  function persistResult(key, r, savedAt, isRefresh) {
    return saveAICache(gridId, key, { result: r, savedAt }, {
      resolution, radius, lat, lng,
      subcategory,   // targets the (grid, business) record — one owned analysis per business
      kode_kelurahan:  zone?.kode_kelurahan ?? null,
      profile_snapshot: profileForFit,
      config_for_gate:  configForGate,
      is_refresh:      !!isRefresh,
    });
  }

  // Fetch analysis when zone or radius changes. Reuse the DB-persisted result
  // (deep_analysis_results.result_json) when present & fresh — owned grids keep a
  // fixed coordinate, so the same grid+radius+context never needs a second call.
  //
  // IMPORTANT: SELECTING a different grid must never silently start a PAID run. If the
  // just-selected grid has a cached result at another radius (but not the carried-over
  // one), we snap the radius to an existing one and show that — no charge. A live
  // (charged) generation only happens on an explicit radius change, or the very first
  // analysis of a grid that has no cached result at all.
  useEffect(() => {
    setResult(null);
    setError(null);
    setGeneratedAt(null);
    setNeedsRun(false);
    if (!gridId || !accessGranted || lat == null || lng == null) return;

    const key = v2CacheKey(gridId, radius, subcategory, target_ses, benchmarks, profile);
    const keyFor = (r) => v2CacheKey(gridId, r, subcategory, target_ses, benchmarks, profile);
    let cancelled = false;
    setLoading(true);

    // Opening a grid never spends money. A stored report is shown whatever its age —
    // staleness is the "Perbarui Data" button's business, and that button confirms
    // the cost first.
    const stored = (hit) => !!hit?.result;

    (async () => {
      // 1) Everything stored for this grid. A FAILED read is not "no cache" — it
      //    used to fall through to the paid call.
      let cache;
      try { cache = (await getAICache(gridId)) || {}; }
      catch {
        if (!cancelled) { setLoading(false); setError("Laporan tersimpan tidak bisa dimuat. Muat ulang halaman — tidak ada biaya yang dikenakan."); }
        return;
      }
      if (cancelled) return;
      cacheRef.current = cache;

      const hit = cache[key];
      if (stored(hit)) {
        liveIntent.current = null;
        setResult(hit.result); setGeneratedAt(hit.savedAt || null); setLoading(false);
        return;
      }

      const intent = liveIntent.current;
      // 2) Only a cost-confirmed radius change may refuse a cached stand-in.
      if (intent !== "confirmed") {
        const avail = RADIUS_OPTIONS.map((r) => ({ r, hit: cache[keyFor(r)] })).filter((x) => stored(x.hit));
        if (avail.length) {
          avail.sort((a, b) => (b.hit.savedAt || 0) - (a.hit.savedAt || 0));
          liveIntent.current = null;
          setLoading(false); setRadius(avail[0].r);
          return;   // the radius change re-runs this effect → exact hit, no charge
        }
        // Stored under another key — an older label spelling, a profile edit, a
        // Branch Radar store's own key. Show it, never charge.
        const anyHit = Object.values(cache).filter(stored).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0))[0];
        if (anyHit) {
          liveIntent.current = null;
          setResult(anyHit.result); setGeneratedAt(anyHit.savedAt || null); setLoading(false);
          return;
        }
        // Nothing stored at all. Without a handed-over intent, ask first.
        if (intent !== "nav") { setLoading(false); setNeedsRun(true); return; }
      }
      liveIntent.current = null;

      // 3) Live call, then persist + log (a confirmed run, or the first analysis of a
      //    grid handed over by Comparison / Skenario).
      if (!cancelled) setScanning(true);   // billed Gemini call begins → block the screen
      try {
        const r = await postDeepAnalysisV2(buildRequest());
        if (cancelled) return;
        if (r?.ok) {
          const savedAt = Date.now();
          setResult(r); setGeneratedAt(savedAt);
          persistResult(key, r, savedAt, false).catch(() => {});
        } else {
          setError(r?.error || "Analisis gagal");
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) { setLoading(false); setScanning(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [gridId, radius, accessGranted, runNonce]);

  // Radius switch: free if that radius is already cached, otherwise it runs a new
  // PAID report — confirm the cost before triggering it.
  async function handleRadiusClick(r) {
    if (r === radius) return;
    const key = v2CacheKey(gridId, r, subcategory, target_ses, benchmarks, profile);
    const hit = cacheRef.current?.[key];
    const cached = hit?.result && (!hit.savedAt || Date.now() - hit.savedAt <= V2_CACHE_TTL);
    if (cached) { setRadius(r); return; }        // no charge → switch instantly
    setRadiusConfirm(r); setRadiusQuote(null); setRadiusQuoteLoading(true);
    try { setRadiusQuote(await getDeepAnalysisQuote(gridId, subcategory, r)); }
    catch { setRadiusQuote(null); }
    finally { setRadiusQuoteLoading(false); }
  }

  // First run of a grid that has nothing stored: quote, then the same confirm modal.
  async function handleRunHere() {
    setRadiusConfirm(radius); setRadiusQuote(null); setRadiusQuoteLoading(true);
    try { setRadiusQuote(await getDeepAnalysisQuote(gridId, subcategory, radius)); }
    catch { setRadiusQuote(null); }
    finally { setRadiusQuoteLoading(false); }
  }

  // Refresh: force a brand-new generation (only when the 3-month gate allows).
  async function handleRefresh() {
    if (!canRefresh || refreshing || !gridId) return;
    setRefreshing(true);
    setError(null);
    const key = v2CacheKey(gridId, radius, subcategory, target_ses, benchmarks, profile);
    try {
      const r = await postDeepAnalysisV2(buildRequest());
      if (r?.ok) {
        const savedAt = Date.now();
        setResult(r); setGeneratedAt(savedAt);
        setRefreshOpen(false);
        await persistResult(key, r, savedAt, true);
      } else {
        setError(r?.error || "Pembaruan gagal");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  }

  const analysis = result?.analysis ?? null;
  const fitView  = buildFitView(result, analysis, target_ses);
  const yourFit  = computeYourFit(fitView?.summary);

  // ── PDF export ──────────────────────────────────────────────────────────────
  async function handleDownloadPDF() {
    if (downloading || !analysis) return;
    setDownloading(true);
    try {
      exportDeepAnalysisPDF({
        analysis, fitView, result, profileView, yourFit, score, idx,
        verdict, gridLabel, fullLocation, namaKabkot, subcategory,
        target_ses, radius, resolution, lat, lng, generatedAt,
        // Export audit log: which grids were printed, when.
        audit: { gridId, meta: {
          resolution, radius, lat, lng,
          kode_kelurahan: zone?.kode_kelurahan ?? null,
          profile_snapshot: profileForFit,
        } },
      });
    } catch (e) {
      console.error("PDF export failed:", e);
      alert(`Pembuatan PDF gagal: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100dvh-84px)]">

      {/* ── Full-screen scan lock (blocks navigation during a billed Gemini call) ── */}
      {busy && <ScanningOverlay refreshing={refreshing} radius={radius} />}

      {/* ── Sidebar: My Grids ─────────────────────────────────────────────── */}
      <aside className="sticky top-0 w-[270px] shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden h-[calc(100dvh-84px)]">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 shrink-0">
          <FolderOpen className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Grid Saya</span>
        </div>

        {/* Category tabs — find grids by focus (Klasik / Bisnis / Iklan) */}
        <div className="flex border-b border-slate-100 shrink-0">
          {[["klasik", "Klasik", ownedZones.length], ["potensi_bisnis", "Bisnis", potensiGrids.potensi_bisnis.length], ["potensi_iklan", "Iklan", potensiGrids.potensi_iklan.length]].map(([id, label, n]) => {
            const active = listTab === id;
            return (
              <button key={id} onClick={() => setListTab(id)}
                className={`flex-1 py-2 text-[11px] font-bold border-b-2 transition-colors ${active ? "border-teal-600 text-teal-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                {label}{n > 0 ? ` (${n})` : ""}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {listTab === "klasik" ? (
            ownedZones.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-3"><FolderOpen className="w-5 h-5 text-slate-300" /></div>
                <p className="text-xs font-semibold text-slate-400">Belum ada grid Klasik</p>
                <p className="text-[10px] text-slate-300 mt-1 leading-relaxed">Buka kunci sebuah zona dari halaman Perbandingan untuk melihatnya di sini.</p>
                <button onClick={() => navigate("/compare")} className="mt-4 text-[10px] font-bold text-teal-600 hover:text-teal-700 transition-colors">Ke Perbandingan →</button>
              </div>
            ) : (
              ownedZones.map((z) => {
                const isActive = activeZone?.gridId === z.gridId && reportTab === "klasik";
                const zScore   = z.score ?? z.siteScore ?? 0;
                const zColor   = GRID_COLORS[tierIdxOf(z, zScore)];
                return (
                  <button key={z.gridId} onClick={() => { setActiveZone(z); setReportTab("klasik"); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 hover:bg-slate-50 transition-colors group ${isActive ? "bg-teal-50 border-l-2 border-l-teal-600" : ""}`}>
                    <div className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: zColor }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-mono font-bold truncate" style={{ color: zColor }}>grid-{z.gridId}</p>
                      {z.namaKelurahan && <p className="text-[10px] font-medium text-slate-600 truncate">{z.namaKelurahan}</p>}
                      <p className="text-[10px] text-slate-400 truncate">{[z.namaKecamatan ? `Kec. ${z.namaKecamatan}` : null, z.namaKabkot].filter(Boolean).join(", ") || "—"}</p>
                      {z.subcategory && <p className="text-[10px] text-slate-400 truncate italic">{z.subcategory}</p>}
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <span className="text-sm font-extrabold leading-none" style={{ color: zColor }}>{Math.floor(zScore)}</span>
                      <button onClick={(e) => { e.stopPropagation(); handleRemoveZone(z.gridId); }}
                        className="text-red-300 hover:text-red-500 transition-colors opacity-50 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </button>
                );
              })
            )
          ) : (
            potensiGrids[listTab].length === 0 ? (
              <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-3"><FolderOpen className="w-5 h-5 text-slate-300" /></div>
                <p className="text-xs font-semibold text-slate-400">Belum ada laporan {listTab === "potensi_bisnis" ? "Potensi Bisnis" : "Potensi Iklan"}</p>
                <p className="text-[10px] text-slate-300 mt-1 leading-relaxed">Buat dari peta Skenario, atau dari tab laporan pada grid mana pun di sini.</p>
              </div>
            ) : (
              potensiGrids[listTab].map((g) => {
                const isActive = activeZone?.gridId === g.gridId && reportTab === listTab;
                const dot = listTab === "potensi_bisnis" ? "#059669" : "#c026d3";
                return (
                  <button key={g.gridId} onClick={() => { setActiveZone({ gridId: g.gridId, lat: g.lat, lng: g.lng, namaKelurahan: g.namaKelurahan, namaKecamatan: g.namaKecamatan, namaKabkot: g.namaKabkot, namaProvinsi: g.namaProvinsi, kode_kelurahan: g.kode_kelurahan, kode_kabkot: g.kode_kabkot }); setReportTab(listTab); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 hover:bg-slate-50 transition-colors ${isActive ? "bg-teal-50 border-l-2 border-l-teal-600" : ""}`}>
                    <div className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: dot }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-mono font-bold truncate" style={{ color: dot }}>grid-{g.gridId}</p>
                      {g.namaKelurahan && <p className="text-[10px] font-medium text-slate-600 truncate">{g.namaKelurahan}</p>}
                      <p className="text-[10px] text-slate-400 truncate">{[g.namaKecamatan ? `Kec. ${g.namaKecamatan}` : null, g.namaKabkot].filter(Boolean).join(", ") || "—"}</p>
                    </div>
                  </button>
                );
              })
            )
          )}
        </div>

        {ownedZones.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 shrink-0">
            {showClearConfirm ? (
              <div className="space-y-2">
                <p className="text-[10px] text-slate-500 text-center">Hapus semua {ownedZones.length} grid?</p>
                <div className="flex gap-2">
                  <button onClick={() => { clearZones(); setActiveZone(null); setShowClearConfirm(false); }}
                    className="flex-1 py-1.5 rounded-lg bg-red-50 text-red-600 text-[10px] font-bold hover:bg-red-100 transition-colors">Hapus semua</button>
                  <button onClick={() => setShowClearConfirm(false)}
                    className="flex-1 py-1.5 rounded-lg bg-slate-50 text-slate-500 text-[10px] font-bold hover:bg-slate-100 transition-colors">Batal</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowClearConfirm(true)}
                className="w-full text-[10px] text-slate-400 hover:text-red-400 transition-colors font-medium py-1">Hapus semua grid yang dimiliki</button>
            )}
          </div>
        )}
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 bg-slate-100">
        {!gridId ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
              <Lock className="h-7 w-7 text-slate-300" />
            </div>
            <p className="mb-2 text-xl font-bold text-slate-500">Belum ada zona dipilih</p>
            <p className="mb-6 max-w-xs text-sm text-slate-400">
              Buka <strong className="text-slate-600">Perbandingan</strong>, pilih sebuah zona, lalu klik "Analisis Mendalam" untuk membuka laporan ini.
            </p>
            <button onClick={() => navigate("/compare")}
              className="flex items-center gap-2 rounded-xl bg-teal-700 px-6 py-2.5 text-sm font-bold text-white hover:bg-teal-800 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Kembali ke Perbandingan
            </button>
          </div>
        ) : (
          <div>
            {/* Hero — only for Klasik; Potensi reports have no sitescore/fit. */}
            {reportTab === "klasik" && (
            <DeepAnalysisHero
              gridLabel={gridLabel} fullLocation={fullLocation} result={result}
              resolution={resolution} lat={lat} lng={lng} verdict={verdict}
              score={score} idx={idx} yourFit={yourFit} subcategory={subcategory}
              targetSes={target_ses} radius={radius} generatedAt={generatedAt}
              onBack={() => navigate("/compare")}
            />
            )}

            {/* Content */}
            <div className="max-w-6xl mx-auto space-y-5 px-8 py-8 pb-24">

              {/* Report-type tabs — Klasik + the two Skenario reports for this grid */}
              <div className="flex gap-1 border-b border-slate-200">
                {[["klasik", "Analisis Klasik", null], ["potensi_bisnis", "Potensi Bisnis", Store], ["potensi_iklan", "Potensi Iklan", Megaphone]].map(([id, label, Icon]) => {
                  const active = reportTab === id;
                  return (
                    <button key={id} onClick={() => setReportTab(id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-bold border-b-2 -mb-px transition-colors ${active ? "border-teal-600 text-teal-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
                    </button>
                  );
                })}
              </div>

              {reportTab === "potensi_bisnis" ? (
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden"><PotensiTypeView key="potensi_bisnis" type="potensi_bisnis" grid={gridCtx} /></div>
              ) : reportTab === "potensi_iklan" ? (
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden"><PotensiTypeView key="potensi_iklan" type="potensi_iklan" grid={gridCtx} /></div>
              ) : (<>

              {/* Radius selector */}
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Radius pencarian</span>
                  <div className="flex gap-1.5">
                    {RADIUS_OPTIONS.map((r) => (
                      <button key={r} onClick={() => handleRadiusClick(r)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${radius === r ? "bg-teal-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-teal-300"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400">dari titik persis yang Anda klik</span>
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-600 leading-snug">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>Setiap radius adalah laporan terpisah. Beralih ke radius yang belum pernah dibuat akan menjalankan pemindaian lapangan baru dan dikenakan biaya lagi.</span>
                </p>
              </div>

              {/* Section 1 — Business Profile (your onboarding selections) */}
              <Section title="Profil Bisnis"
                subtitle="Profil onboarding yang menjadi dasar personalisasi analisis ini"
                icon={Briefcase}>
                <BusinessProfile view={profileView} />
              </Section>

              {(lat == null || lng == null) && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
                  <p className="text-sm font-semibold text-amber-700 mb-1">Belum ada koordinat tersimpan untuk grid ini</p>
                  <p className="text-xs text-amber-600">
                    Grid tersimpan ini belum memiliki titik pasti. Buka dari halaman
                    <button onClick={() => navigate("/compare")} className="mx-1 underline font-semibold">Perbandingan</button>
                    untuk menjalankan pemindaian lapangan dari titik yang Anda klik.
                  </p>
                </div>
              )}

              {loading && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 flex items-center gap-3 text-sm text-slate-500">
                  <Spinner /> <span>Memindai lapangan di sekitar titik ini…</span>
                </div>
              )}

              {needsRun && !loading && !error && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <p className="text-sm font-semibold text-slate-800 mb-1">Laporan untuk grid ini belum dibuat</p>
                  <p className="text-xs text-slate-500 mb-4">Menjalankannya memindai lapangan secara langsung dan dikenakan biaya. Biayanya ditampilkan dulu sebelum apa pun dijalankan.</p>
                  <button onClick={handleRunHere}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand-700 hover:bg-brand-800 transition-colors">
                    Lihat biaya &amp; jalankan analisis
                  </button>
                </div>
              )}

              {error && !loading && (
                <div className="rounded-2xl border border-red-100 bg-red-50/60 p-5">
                  <p className="text-sm font-semibold text-red-600 mb-1">Analisis lapangan tidak tersedia</p>
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              {analysis && !loading && (
                <>
                  <DeepAnalysisReport result={result} targetSes={target_ses} radius={radius} />

                  {/* Footer: provenance + Refresh + PDF export */}
                  <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                      <Building2 className="w-3 h-3" />
                      Berdasarkan data Google Maps langsung. Jarak bersifat perkiraan — verifikasi di lapangan sebelum memutuskan.
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setRefreshOpen(true)}
                        className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                        <RefreshCw className="w-3.5 h-3.5" /> Perbarui Data
                      </button>
                      <button onClick={() => setShareOpen(true)}
                        className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                        <Share2 className="w-3.5 h-3.5" /> Bagikan
                      </button>
                      <button onClick={handleDownloadPDF} disabled={downloading}
                        className="flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 text-xs font-bold text-white hover:bg-teal-800 transition-colors disabled:opacity-50">
                        <Download className="w-3.5 h-3.5" />
                        {downloading ? "Membuat PDF…" : "Unduh Laporan PDF"}
                      </button>
                      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)}
                        kind="klasik" gridId={gridId} title={`${gridLabel} · ${fullLocation || namaKabkot || ""}`}
                        ownerLabel={profile?.businessName || null}
                        buildPayload={() => deepAnalysisTicket({
                          analysis, fitView, result, profileView, yourFit, score, idx,
                          verdict, gridLabel, fullLocation, namaKabkot, subcategory,
                          target_ses, radius, resolution, lat, lng, generatedAt,
                        })} />
                    </div>
                  </div>
                </>
              )}

              </>)}
            </div>
          </div>
        )}
      </div>

      {/* ── Radius-change confirmation (uncached radius = a new paid report) ──── */}
      {radiusConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center mb-3">
                <Compass className="w-5 h-5 text-teal-600" />
              </div>
              <h2 className="text-base font-bold text-slate-800 mb-1">Jalankan laporan radius baru?</h2>
              <p className="text-xs text-slate-500 leading-relaxed">
                Radius <span className="font-semibold text-slate-700">{radiusConfirm}</span> belum pernah
                dibuat untuk lokasi & bisnis ini, jadi ini menjalankan pemindaian lapangan baru
                (memanggil AI + data) dan <span className="font-semibold text-slate-700">dikenakan biaya</span>.
              </p>
            </div>
            <div className="mx-6 mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              {radiusQuoteLoading ? (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Menghitung biaya…
                </div>
              ) : radiusQuote?.billing_enabled === false ? (
                <p className="text-[11px] text-slate-500">Penagihan nonaktif — laporan ini gratis di lingkungan ini.</p>
              ) : radiusQuote ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{radiusQuote.is_first ? "Biaya laporan baru" : "Biaya laporan"}</span>
                  <span className="text-sm font-extrabold text-slate-800 tabular-nums">
                    Rp {Math.round(radiusQuote.price || 0).toLocaleString("id-ID")}
                  </span>
                </div>
              ) : (
                <p className="text-[11px] text-amber-700">Tidak bisa memuat biaya. Kamu akan ditagih sesuai tarif saat menjalankan.</p>
              )}
            </div>
            <div className="px-6 pb-6 flex gap-2">
              <button onClick={() => { setRadiusConfirm(null); setRadiusQuote(null); }}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Batal
              </button>
              <button onClick={() => { const r = radiusConfirm; setRadiusConfirm(null); setRadiusQuote(null); liveIntent.current = "confirmed"; if (r === radius) setRunNonce((n) => n + 1); else setRadius(r); }}
                disabled={radiusQuoteLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-teal-700 hover:bg-teal-800 transition-colors disabled:opacity-50">
                Jalankan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Refresh confirmation popup ──────────────────────────────────────── */}
      {refreshOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center mb-3">
                <RefreshCw className="w-5 h-5 text-teal-600" />
              </div>
              <h2 className="text-base font-bold text-slate-800 mb-1">Perbarui data analisis mendalam?</h2>
              <p className="text-xs text-slate-500 leading-relaxed">
                Ini akan menjalankan ulang analisis lapangan dari awal dan menimpa hasil tersimpan
                untuk <span className="font-semibold text-slate-700">{gridLabel}</span> pada radius
                <span className="font-semibold text-slate-700"> {radius}</span>.
              </p>
            </div>

            <div className={`mx-6 mb-4 rounded-xl border px-4 py-3 ${canRefresh ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50/70"}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Terakhir dibuat</span>
                <span className="font-semibold text-slate-700">{fmtGenerated(generatedAt) || "—"}</span>
              </div>
              <p className={`text-[11px] mt-2 leading-relaxed ${canRefresh ? "text-slate-400" : "text-amber-700"}`}>
                {canRefresh
                  ? `Data dapat diperbarui (sudah lebih dari ${refreshGateDays} hari).`
                  : `Pembaruan untuk radius & profil ini tersedia setelah laporan berusia ${refreshGateDays} hari. Pembaruan berikutnya pada ${fmtGenerated(nextRefreshAt) || "—"}. Mengubah radius atau pengaturan profil akan langsung menjalankan laporan baru.`}
              </p>
            </div>

            <div className="px-6 pb-6 flex gap-2">
              <button onClick={() => setRefreshOpen(false)} disabled={refreshing}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
                Batal
              </button>
              <button onClick={handleRefresh} disabled={!canRefresh || refreshing}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: canRefresh ? "#0f766e" : "#9ca3af" }}>
                {refreshing ? "Memperbarui…" : "Perbarui sekarang"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
